import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { ChevronLeft, ChevronRight, ChevronDown, Plus, X, Clock, DollarSign, Trash2, Copy, CalendarPlus, Route as RouteIcon, GripVertical } from 'lucide-react';
import { toast } from 'react-toastify';
import { copyOpenShiftLink } from '../utils/openShiftLink';
import CustomerIntake from '../components/CustomerIntake';
import CreateJobModal from '../components/CreateJobModal';
import PeoplePicker from '../components/PeoplePicker';
import OpenShiftDrafts from '../components/OpenShiftDrafts';
import GiveShiftTo from '../components/GiveShiftTo';
import PeopleNeededInput from '../components/PeopleNeededInput';
import { fetchOfferJobs, offerJobsKey, jobsWord, allJobsWord, shortDay, namesWord, whyNotWord } from '../utils/openShiftDay';
import { fmtClock, businessToday } from '../utils/time';
import { useReadOnly } from '../hooks/useReadOnly';
import { PlanPromised } from '../components/DayPlanNotes';
import { clock12 } from '../utils/dayPlan';
import AccountMark from '../components/AccountMark';
import TodoSheet from '../components/TodoSheet';

// ── Calendar constants ────────────────────────────────────────────────────────
const HOUR_HEIGHT = 64; // px per hour
const START_HOUR = 5;   // 5 AM
const END_HOUR   = 22;  // 10 PM
const TOTAL_HOURS = END_HOUR - START_HOUR;

// ── Date helpers ─────────────────────────────────────────────────────────────
// Use local date components to avoid UTC shift bugs for non-UTC timezones
function isoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(d: Date, n: number) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function startOfWeek(d: Date) { const r = new Date(d); r.setDate(r.getDate() - r.getDay()); return r; }

function timeToMinutes(t: string) {
  if (!t) return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minutesToPx(mins: number) { return ((mins - START_HOUR * 60) / 60) * HOUR_HEIGHT; }
// ── Height from ETA + duration ────────────────────────────────────────────────
function durationToPx(estimatedMinutes: number | null) {
  if (!estimatedMinutes) return 36;
  return Math.max((estimatedMinutes / 60) * HOUR_HEIGHT, 24);
}

// ── Service type helpers ──────────────────────────────────────────────────────
// Three kinds of work live on the calendar (the owner's words, Sept 2026):
//   mowing  — route stops, folded into one block per route      (green)
//   quote   — an in-person visit to learn what a customer needs (blue)
//   project — everything else that is booked work               (orange)
// Written bids (the line-item documents) are not events and never show here.
type Category = 'mowing' | 'quote' | 'project' | 'todo';
function categoryOf(j: any): Category {
  const t = String(j?.service_type || '').toLowerCase();
  if (t === 'todo') return 'todo';
  if (t === 'estimate' || t === 'quote') return 'quote';
  if (t === 'mowing' || (j?.route_id && !j?.is_open_shift)) return 'mowing';
  return 'project';
}
const CAT: Record<Category, { label: string; hex: string; card: string; chip: string }> = {
  mowing:  { label: 'Mowing',   hex: '#16a34a', card: 'bg-green-100 border-green-500 text-green-900',    chip: 'bg-green-100 text-green-700 border-green-300' },
  quote:   { label: 'Quotes',   hex: '#2563eb', card: 'bg-blue-100 border-blue-500 text-blue-900',       chip: 'bg-blue-100 text-blue-700 border-blue-300' },
  project: { label: 'Projects', hex: '#f97316', card: 'bg-orange-100 border-orange-500 text-orange-900', chip: 'bg-orange-100 text-orange-700 border-orange-300' },
  // Errands and little things for the office — not customer work, never billed or routed.
  todo:    { label: 'To-dos',   hex: '#64748b', card: 'bg-slate-100 border-slate-500 text-slate-800',    chip: 'bg-slate-100 text-slate-700 border-slate-300' },
};

// A bid's agreed start day, before the customer signs (services/bidHolds; owner, Oct 22 2026: "anyone who has a
// starting day in a bid, put a yellow section on the calendar to show if they accept that spot is taken"). Not a
// job: never dragged, counted, paid or shown to the crew. Tap it and the bid opens.
type Hold = { quote_id: number; customer_id: number | null; customer_name: string; address: string | null; date: string;
  time_start: string | null; kind: string; kind_words: string; minutes: number | null; title: string; status: string };
const HOLD = { hex: '#eab308', card: 'bg-yellow-100 border-yellow-500 text-yellow-900', chip: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
// A winter job still on its placeholder day (jobs.winter_hold; services/winterWork): the customer chose to have the work
// done between Dec 1 and Mar 1 at 10% off, and the office has not set the real date yet. A real job — billed when done —
// drawn cool-blue and dashed so it never reads as a day that is settled. Set the date on the Winter work page.
const WINTER = { hex: '#0284c7', card: 'bg-sky-100 border-sky-500 border-dashed text-sky-900', words: 'Winter work — Dec 1 to Mar 1 — date to be set' };
const isWinterHold = (j: any) => Number(j?.winter_hold) === 1 && j?.status !== 'complete';
const holdsWord = (n: number) => `${n} hold${n === 1 ? '' : 's'}`;

function formatServiceType(st: string | null | undefined): string {
  if (!st) return 'Job';
  const map: Record<string, string> = {
    mowing: 'Mowing', project: 'Project', landscaping: 'Landscaping',
    mulch: 'Mulch', cleanup: 'Cleanup', irrigation: 'Irrigation',
    fence: 'Fence', trimming: 'Bush Trimming', bush_trimming: 'Bush Trimming',
    'bush trimming': 'Bush Trimming', fertilizer: 'Fertilizer',
    fertilizing: 'Fertilizer', aeration: 'Aeration', seeding: 'Seeding',
    sod: 'Sod', grading: 'Grading', drainage: 'Drainage', other: 'Other',
  };
  const key = st.toLowerCase();
  return map[key] || st.split(/[_\s]+/).map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
}

// ── Overlap layout: returns per-job { leftPct, widthPct, hidden } ─────────────
// "00:00" is a DB sentinel for "no time set" — treat it as untimed
function hasRealTime(t: string | null | undefined): boolean {
  return !!t && t !== '00:00';
}

function computeEventLayout(dayJobs: any[], maxCols = 3): Map<number, { leftPct: number; widthPct: number; hidden?: boolean }> {
  const layout = new Map<number, { leftPct: number; widthPct: number; hidden?: boolean }>();


  const timed = dayJobs
    .filter(j => hasRealTime(j.time_start))
    .map(j => ({
      id: j.id,
      _s: timeToMinutes(j.time_start),
      _e: timeToMinutes(j.time_start) + (j.estimated_minutes_snapshot || 45),
    }))
    .sort((a, b) => a._s - b._s);

  // Group into overlap clusters (connected components in the interval graph)
  const clusters: typeof timed[] = [];
  for (const ev of timed) {
    const hits = clusters.reduce<number[]>((acc, c, i) => {
      if (c.some(x => x._s < ev._e && x._e > ev._s)) acc.push(i);
      return acc;
    }, []);
    if (!hits.length) {
      clusters.push([ev]);
    } else {
      const merged = [ev, ...hits.flatMap(i => clusters[i])];
      hits.slice().reverse().forEach(i => clusters.splice(i, 1));
      clusters.push(merged);
    }
  }

  // Assign columns greedily within each cluster; cap at MAX_EVENT_COLS
  for (const cluster of clusters) {
    cluster.sort((a, b) => a._s - b._s);
    const cols: number[] = [];
    const jobCol = new Map<number, number>();
    for (const ev of cluster) {
      let c = cols.findIndex(end => ev._s >= end);
      if (c === -1) c = cols.length;
      cols[c] = ev._e;
      jobCol.set(ev.id, c);
    }
    const visibleCols = Math.min(cols.length, maxCols);
    jobCol.forEach((c, id) => {
      if (c < maxCols) {
        layout.set(id, { leftPct: (c / visibleCols) * 100, widthPct: (1 / visibleCols) * 100 });
      } else {
        layout.set(id, { leftPct: 0, widthPct: 0, hidden: true });
      }
    });
  }

  // Untimed events: show up to maxCols side-by-side, rest hidden
  const untimed = dayJobs.filter(j => !hasRealTime(j.time_start));
  const visibleUnt = Math.min(untimed.length, maxCols);
  untimed.forEach((j, c) => {
    if (c < maxCols) {
      layout.set(j.id, { leftPct: (c / visibleUnt) * 100, widthPct: (1 / visibleUnt) * 100 });
    } else {
      layout.set(j.id, { leftPct: 0, widthPct: 0, hidden: true });
    }
  });

  return layout;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CalendarPage() {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Month first on a phone (the Apple Calendar shape: grid of dots, the day's
  // list underneath); the hour grid stays the default on a desktop.
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'month' : 'week'));
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selectedDay, setSelectedDay] = useState(new Date());
  // Month view: drag across days to pick several (owner, Oct 6 2026: "multi day selection by dragging the days").
  // selectedDay is where the drag started; rangeEnd where it is now. null = one day, as before.
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const dragRef = React.useRef<{ from: Date; moved: boolean } | null>(null);
  const [jobModal, setJobModal] = useState<{ date: string; time?: string; prefill?: any; cutJobId?: number } | null>(null);
  const [todoModal, setTodoModal] = useState<{ date: string } | null>(null);
  // A quote visit booked from the calendar, and the phone-calendar link sheet.
  const [quoteModal, setQuoteModal] = useState<{ date: string; customer_id?: number } | null>(null);
  const [phoneFeed, setPhoneFeed] = useState(false);
  // Route the day's loose jobs (owner, Sep 17 2026).
  const [routeDay, setRouteDay] = useState<string | null>(null);
  // …opened ready to offer them as one open shift (the job page's "Offer the day's jobs as one open shift" link).
  const [routeDayOffer, setRouteDayOffer] = useState(false);
  const [viewJobId, setViewJobId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  // Selection + clipboard state
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [clipboard, setClipboard] = useState<{ job: any; isCut: boolean } | null>(null);
  const [pasteTargetDate, setPasteTargetDate] = useState<string>(isoDate(new Date()));

  // Filters
  const [filters, setFilters] = useState({
    showMowing: true,
    showProjects: true,
    showQuotes: true,
    showTodos: true,
    showOpenShifts: true,
    showHolds: true,
    showComplete: true,
    showSkipped: false,
  });

  // Routes start folded: one block per route, tap the name to see its stops.
  const [expandedRoutes, setExpandedRoutes] = useState<Set<string>>(new Set());
  const toggleRoute = (key: string) => setExpandedRoutes(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  // Drag state
  const [draggingJob, setDraggingJob] = useState<any | null>(null);
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [dragOverRoute, setDragOverRoute] = useState<string | null>(null);   // `${date}:${routeId}` under the pointer
  // The same drag by pointer events, for a phone (owner, Oct 13 2026: "drag a job on the calendar to a route, drag
  // and drop it on mobile"). The HTML5 drag above never fires on a touch screen. A press on a job's grip lifts it
  // (the grip is touch-action: none, so the page under it still scrolls from anywhere else), a ghost follows the
  // finger, and on release whatever is under it takes the drop: a day cell or column (data-drop="day"), a route row
  // or block (data-drop="route"), or the "off the route" bar (data-loose) — through the same /move the mouse drag uses.
  // While a finger holds a job the page does not scroll (a touchmove listener refuses it: on an iPhone touch-action
  // alone is not enough); a finger near the top or bottom of the day column or the screen scrolls it instead.
  const [ptrDrag, setPtrDrag] = useState<{ job: any; x: number; y: number } | null>(null);
  const [dragOverLoose, setDragOverLoose] = useState(false);                 // the finger is over the "off the route" bar
  const ptrRef = useRef<{ job: any; pointerId: number; el: HTMLElement; scroller: HTMLElement | null } | null>(null);
  const ptrPointRef = useRef<{ x: number; y: number } | null>(null);        // where the finger is, for the edge scroll
  const ptrCleanupRef = useRef<(() => void) | null>(null);                  // undoes the document listeners of a finger drag
  const autoScrollRef = useRef<number | null>(null);                        // the requestAnimationFrame of the edge scroll
  const autoScrollTickRef = useRef<() => void>(() => {});                   // the latest tick, so a frame never runs a stale one
  useEffect(() => () => { ptrCleanupRef.current?.(); if (autoScrollRef.current) cancelAnimationFrame(autoScrollRef.current); }, []);
  // On a touch screen the cards are not HTML5-draggable at all: Chrome starts a native drag of a draggable element
  // on a long press and cancels the pointer events the grip listens to, so the finger drag would die half-way.
  const coarsePointer = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const mouseDrag = !readOnly && !coarsePointer;

  // Deliberately NOT auto-generating recurring jobs on mount anymore — opening
  // a calendar must never write hundreds of rows. Visits generate when a
  // schedule is created/edited, and on demand from the Routes page.

  // Pre-fill from URL params
  const preCustomer = searchParams.get('customer') || '';
  const preProperty = searchParams.get('property') || '';

  // Auto-open create modal when navigating from Customers page with ?customer=X,
  // or the quote-visit sheet when My Day sends us here with ?quote=1.
  useEffect(() => {
    if (preCustomer && !jobModal && searchParams.get('quote') !== '1') {
      setJobModal({ date: isoDate(new Date()) });
    }
    if (searchParams.get('quote') === '1') setQuoteModal({ date: isoDate(new Date()), customer_id: preCustomer ? Number(preCustomer) : undefined });
    const rd = searchParams.get('route_day');
    if (rd) {
      const day = /^\d{4}-\d{2}-\d{2}$/.test(rd) ? rd : isoDate(new Date());
      setRouteDay(day);
      // The calendar behind the sheet shows that day too, so what gets built is in view when it closes.
      const at = new Date(day + 'T12:00:00');
      setSelectedDay(at);
      setWeekStart(startOfWeek(at));
      setRouteDayOffer(searchParams.get('offer') === '1');
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const displayDays = viewMode === 'week' ? weekDays : [selectedDay];

  // Month grid: 6 rows of 7 starting on the Sunday before the 1st.
  const monthStart = new Date(selectedDay.getFullYear(), selectedDay.getMonth(), 1);
  const gridStart = startOfWeek(monthStart);
  const gridDays = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  // Date range for query
  const queryStart = isoDate(viewMode === 'month' ? gridStart : viewMode === 'week' ? weekStart : selectedDay);
  const queryEnd   = isoDate(viewMode === 'month' ? addDays(gridStart, 41) : viewMode === 'week' ? addDays(weekStart, 6) : selectedDay);

  const { data: jobsData } = useQuery(['calendar-jobs', queryStart, queryEnd], async () => {
    if (viewMode === 'month') {
      // One request for the whole grid instead of one per day.
      return api.get('/jobs', { params: { from: queryStart, to: queryEnd, limit: 2000, include_estimates: 1 } }).then(r => r.data.jobs as any[]);
    }
    const promises = displayDays.map(d =>
      api.get('/jobs', { params: { date: isoDate(d), include_estimates: 1 } }).then(r => r.data.jobs as any[])
    );
    const results = await Promise.all(promises);
    return results.flat();
  });

  // Route info for calendar coloring + grouping
  const { data: routesData } = useQuery('routes-for-calendar', () =>
    api.get('/routes').then(r => r.data.routes as any[])
  );
  const routeInfoMap: Record<number, { color: string; planned_start_time: string; name: string }> = {};
  for (const r of (routesData || [])) {
    if (r.id) routeInfoMap[r.id] = {
      color: r.color || '#7c3aed',
      planned_start_time: r.planned_start_time || '08:00',
      name: r.name || `Route ${r.id}`,
    };
  }



  // The holds — one per open bid with an agreed start day in view (GET /calendar/holds).
  const { data: holdsData } = useQuery(['calendar-holds', queryStart, queryEnd], () =>
    api.get('/calendar/holds', { params: { from: queryStart, to: queryEnd } }).then(r => (r.data.holds || []) as Hold[])
  );
  const holdsForDay = (date: string): Hold[] => filters.showHolds ? (holdsData || []).filter(h => h.date === date) : [];
  const openHold = (h: Hold) => navigate(`/quotes?open=${h.quote_id}`);

  const jobs = jobsData || [];
  const jobsForDay = (date: string) => {
    return jobs.filter((j: any) => {
      if (j.scheduled_date !== date) return false;
      if (j.status === 'complete' && !filters.showComplete) return false;
      if (j.status === 'skipped' && !filters.showSkipped) return false;
      if (j.is_open_shift && !filters.showOpenShifts) return false;
      const cat = categoryOf(j);
      if (!filters.showMowing && cat === 'mowing') return false;
      if (!filters.showProjects && cat === 'project') return false;
      if (!filters.showQuotes && cat === 'quote') return false;
      if (!filters.showTodos && cat === 'todo') return false;
      return true;
    });
  };

  // ── Navigation ────────────────────────────────────────────────────────────────
  function prevPeriod() {
    setRangeEnd(null);
    if (viewMode === 'month') setSelectedDay(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    else if (viewMode === 'week') setWeekStart(d => addDays(d, -7));
    else setSelectedDay(d => addDays(d, -1));
  }
  function nextPeriod() {
    setRangeEnd(null);
    if (viewMode === 'month') setSelectedDay(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    else if (viewMode === 'week') setWeekStart(d => addDays(d, 7));
    else setSelectedDay(d => addDays(d, 1));
  }
  function goToday() {
    const now = new Date();
    setRangeEnd(null);
    setWeekStart(startOfWeek(now));
    setSelectedDay(now);
  }

  // ── Delete mutation (used by both keyboard and recurring modal) ───────────────
  const deleteJobMutation = useMutation(async ({ id, mode, force }: { id: number; mode?: string; force?: boolean }) => {
    let url = `/jobs/${id}`;
    const params: string[] = [];
    if (force) params.push('force=true');
    if (mode) params.push(`mode=${mode}`);
    if (params.length) url += '?' + params.join('&');
    await api.delete(url);
  }, {
    onSuccess: () => { qc.invalidateQueries('calendar-jobs'); toast.success('Job deleted'); setSelectedJobId(null); setDeleteTarget(null); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to delete job'); },
  });

  function requestDelete(job: any) {
    if (job.status === 'complete') {
      if (!window.confirm('This job is completed. Delete it anyway?')) return;
      deleteJobMutation.mutate({ id: job.id, force: true });
    } else if (job.recurring_schedule_id) {
      setDeleteTarget(job);
    } else {
      if (!window.confirm('Delete this job?')) return;
      deleteJobMutation.mutate({ id: job.id });
    }
  }

  // ── Keyboard shortcuts ────────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'Escape') { setSelectedJobId(null); setViewJobId(null); return; }
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key === 'v' && clipboard) {
        e.preventDefault();
        setJobModal({
          date: pasteTargetDate,
          prefill: {
            customer_id: String(clipboard.job.customer_id),
            property_id: String(clipboard.job.property_id || ''),
            crew_id: String(clipboard.job.crew_id || ''),
            assigned_crew_member_id: String(clipboard.job.assigned_crew_member_id || ''),
            time_start: clipboard.job.time_start || '',
            notes: clipboard.job.notes || '',
            title: clipboard.job.title || '',
            service_type: clipboard.job.service_type || '',
          },
          cutJobId: clipboard.isCut ? clipboard.job.id : undefined,
        });
        setClipboard(null);
        return;
      }
      if (!selectedJobId) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isMod) {
        e.preventDefault();
        const job = jobs.find((j: any) => j.id === selectedJobId);
        if (job) requestDelete(job);
        return;
      }
      if (isMod && e.key === 'c') {
        e.preventDefault();
        const job = jobs.find((j: any) => j.id === selectedJobId);
        if (job) { setClipboard({ job, isCut: false }); toast.info('Job copied — ⌘V to paste'); }
        return;
      }
      if (isMod && e.key === 'x') {
        e.preventDefault();
        const job = jobs.find((j: any) => j.id === selectedJobId);
        if (job) { setClipboard({ job, isCut: true }); toast.info('Job cut — ⌘V to paste'); setSelectedJobId(null); }
        return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobId, clipboard, pasteTargetDate, jobs]);

  // ── Click on time slot ────────────────────────────────────────────────────────
  function handleSlotClick(date: string, e: React.MouseEvent<HTMLDivElement>) {
    if (draggingJob) return; // ignore click during drag
    if (viewMode === 'week') {
      // In week view: clicking the background drills into that day so events are easier to tap.
      // Use day-view for create/edit actions — don't open create modal from cramped week columns.
      setSelectedDay(new Date(date + 'T12:00:00'));
      setViewMode('day');
      return;
    }
    // In day view: clicking the time grid creates a new job at that time
    setSelectedJobId(null);
    setPasteTargetDate(date);
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const totalMinutes = Math.round((y / HOUR_HEIGHT) * 60 + START_HOUR * 60);
    const hour = Math.floor(totalMinutes / 60);
    const min = Math.round((totalMinutes % 60) / 15) * 15;
    const time = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    setJobModal({ date, time });
  }

  // ── Drag-and-drop ─────────────────────────────────────────────────────────────
  // POST /jobs/:id/move rather than PUT /jobs/:id. The PUT changes the date but
  // never moves the route_jobs stop, so a drag between routes left a phantom stop
  // behind on the old one; it also happily moved completed jobs, which rewrites
  // the day work was done and the revenue hanging off it.
  //
  // Owner's rule (Sep 17 2026, replacing Sep 15): "if it's dragged off the
  // route, no — if it's dropped on a route, yes."
  //   Dropped on a route block → onto that route, at its best spot in the order.
  //   Dropped anywhere else    → off any route, a standalone job at that time.
  //     (An open shift moved within its own day keeps its route: it is only a
  //     time change, and open shifts are drawn outside the route block.)
  const dragUpdate = useMutation(
    async ({ id, scheduled_date, time_start, from_date, to_route_id, keep_route }: {
      id: number; scheduled_date: string; time_start?: string; from_date?: string; to_route_id?: number; keep_route?: boolean;
    }) => {
      const body: any = to_route_id
        ? { scheduled_date, route_id: to_route_id, best_spot: true }
        : { scheduled_date, time_start, adopt_route: false, ...(keep_route ? {} : { route_id: null }) };
      const { data } = await api.post(`/jobs/${id}/move`, body);
      return data;
    },
    {
      onSuccess: (data: any) => {
        qc.invalidateQueries('calendar-jobs');
        qc.invalidateQueries('attention');
        if (data?.message) toast.success(data.message);
      },
      onError: (err: any) => {
        // A refusal here is informative — say why rather than "failed".
        toast.error(err?.response?.data?.message || 'Failed to reschedule job');
      },
    }
  );

  function handleDragStart(e: React.DragEvent, job: any) {
    setDraggingJob(job);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e: React.DragEvent, date: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(date);
  }

  /** The time a drop's height in a day column means, to the quarter hour. */
  function timeAtY(y: number) {
    const totalMins = Math.round((y / HOUR_HEIGHT) * 60 + START_HOUR * 60);
    const clampedMins = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - 15, totalMins));
    const hour = Math.floor(clampedMins / 60);
    const min = Math.round((clampedMins % 60) / 15) * 15;
    return `${String(hour).padStart(2, '0')}:${String(min === 60 ? 0 : min).padStart(2, '0')}`;
  }

  /** Dropped on a day: that date, off any route (an open shift within its own day keeps its route). A column drop sets the time; a month cell keeps it.
   *  `loose` is the "off the route" bar under a finger drag: the same day, the same time, no route. */
  function dropOnDay(job: any, date: string, y: number | null, loose = false) {
    const sameDay = String(job.scheduled_date || '').slice(0, 10) === date;
    if (sameDay && y == null && !loose) return;   // put back where it was
    dragUpdate.mutate({
      id: job.id, scheduled_date: date, time_start: y == null ? undefined : timeAtY(y), from_date: job.scheduled_date,
      keep_route: sameDay && !!job.is_open_shift && !loose,
    });
  }

  /** Dropped on a route: that route, that day, best spot in the order. */
  function dropOnRoute(job: any, date: string, routeId: number) {
    const kind = categoryOf(job);
    if (kind === 'todo' || kind === 'quote') {
      toast.info(kind === 'todo' ? 'To-dos do not go on a route' : 'Quote visits do not go on a route');
      return;
    }
    if (Number(job.route_id) === Number(routeId) && String(job.scheduled_date || '').slice(0, 10) === date) return;
    dragUpdate.mutate({ id: job.id, scheduled_date: date, from_date: job.scheduled_date, to_route_id: routeId });
  }

  function handleDrop(e: React.DragEvent, date: string) {
    e.preventDefault();
    const job = draggingJob;
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    setDraggingJob(null);
    setDragOverDate(null);
    setDragOverRoute(null);
    if (job) dropOnDay(job, date, y);
  }

  /** Dropped on a route block: that route, that day, best spot in the order. */
  function handleRouteDrop(e: React.DragEvent, date: string, routeId: number) {
    e.preventDefault();
    e.stopPropagation();
    const job = draggingJob;
    setDraggingJob(null);
    setDragOverDate(null);
    setDragOverRoute(null);
    if (job) dropOnRoute(job, date, routeId);
  }

  function handleDragEnd() {
    setDraggingJob(null);
    setDragOverDate(null);
    setDragOverRoute(null);
  }

  // ── The same drag by pointer, for a phone ────────────────────────────────────
  /** What is under a point: a day (cell or column), a route (row or block) or the "off the route" bar (loose), from the data-drop marks. */
  function dropTargetAt(x: number, y: number) {
    const el = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest('[data-drop]') as HTMLElement | null;
    if (!el) return null;
    return { kind: el.dataset.drop as 'day' | 'route', date: el.dataset.date || '', routeId: Number(el.dataset.route) || null, timed: el.dataset.timed === '1', loose: el.dataset.loose === '1', el };
  }
  const canLift = (j: any) => !readOnly && !['complete', 'skipped', 'in_progress'].includes(String(j?.status || ''));
  /** The nearest ancestor that scrolls (the day view's 70vh column), else null: the page itself. */
  function scrollParentOf(el: HTMLElement | null) {
    for (let n = el?.parentElement || null; n; n = n.parentElement) {
      const o = getComputedStyle(n).overflowY;
      if ((o === 'auto' || o === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
    }
    return null;
  }
  /** Light up what is under the finger. Functional sets, because the edge scroll calls this from a frame, not a render. */
  function showDropAt(x: number, y: number) {
    const t = dropTargetAt(x, y);
    const loose = !!(t?.kind === 'day' && t.loose);
    const rKey = t?.kind === 'route' && t.routeId ? `${t.date}:${t.routeId}` : null;
    const dKey = t?.kind === 'day' && !loose ? t.date : null;
    setDragOverRoute(prev => (prev === rKey ? prev : rKey));
    setDragOverDate(prev => (prev === dKey ? prev : dKey));
    setDragOverLoose(prev => (prev === loose ? prev : loose));
  }
  // The edge scroll. The page cannot scroll under a finger drag (liftByPointer refuses it), so a finger held near the
  // top or bottom of the day view's scrolling column, or of the screen, scrolls it instead — a route block can run
  // past the screen on a phone, and the drop target is wherever it is.
  const EDGE_PX = 64, EDGE_STEP = 14;
  const edgeStep = (pos: number, lo: number, hi: number) =>
    pos < lo + EDGE_PX ? -Math.ceil(((lo + EDGE_PX - pos) / EDGE_PX) * EDGE_STEP)
    : pos > hi - EDGE_PX ? Math.ceil(((pos - (hi - EDGE_PX)) / EDGE_PX) * EDGE_STEP) : 0;
  const autoScrollTick = () => {
    const cur = ptrRef.current, p = ptrPointRef.current;
    if (!cur || !p) { autoScrollRef.current = null; return; }
    let moved = false;
    if (cur.scroller) {
      // Only while the finger is inside the column: resting on the bar below it must not keep the column rolling.
      const r = cur.scroller.getBoundingClientRect();
      const inside = p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;
      const step = inside ? edgeStep(p.y, Math.max(r.top, 0), Math.min(r.bottom, window.innerHeight)) : 0;
      if (step) { const was = cur.scroller.scrollTop; cur.scroller.scrollTop = was + step; moved = cur.scroller.scrollTop !== was; }
    }
    if (!moved) {
      const step = edgeStep(p.y, 0, window.innerHeight);
      if (step) { const was = window.scrollY; window.scrollBy(0, step); moved = window.scrollY !== was; }
    }
    if (moved) showDropAt(p.x, p.y);
    autoScrollRef.current = requestAnimationFrame(() => autoScrollTickRef.current());
  };
  useEffect(() => { autoScrollTickRef.current = autoScrollTick; });
  function liftByPointer(e: React.PointerEvent, job: any) {
    if (!canLift(job) || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.preventDefault(); e.stopPropagation();
    const el = e.currentTarget as HTMLElement;
    try { el.setPointerCapture(e.pointerId); } catch { /* an older browser: the drag still works while the finger stays on the page */ }
    ptrRef.current = { job, pointerId: e.pointerId, el, scroller: scrollParentOf(el) };
    ptrPointRef.current = { x: e.clientX, y: e.clientY };
    setDraggingJob(job);
    setPtrDrag({ job, x: e.clientX, y: e.clientY });
    if (e.pointerType !== 'mouse') {
      // touch-action: none on the grip is not enough on an iPhone: Safari still scrolls the page (or the day column)
      // under the finger and cancels the drag (owner, Oct 29 2026: "if I drag her down, the screen starts to scroll
      // down as well"). Only a touchmove listener that is not passive can refuse the scroll, and React's are passive,
      // so it goes on the document for the length of the drag — with the long-press menu and the text selection.
      const refuse = (ev: Event) => { if (ptrRef.current) ev.preventDefault(); };
      document.addEventListener('touchmove', refuse, { passive: false });
      document.addEventListener('contextmenu', refuse);
      const body = document.body.style as any;
      const was = { userSelect: body.userSelect, webkitUserSelect: body.webkitUserSelect };
      body.userSelect = 'none'; body.webkitUserSelect = 'none';
      ptrCleanupRef.current = () => {
        document.removeEventListener('touchmove', refuse);
        document.removeEventListener('contextmenu', refuse);
        body.userSelect = was.userSelect; body.webkitUserSelect = was.webkitUserSelect;
      };
    }
    if (autoScrollRef.current) cancelAnimationFrame(autoScrollRef.current);
    autoScrollRef.current = requestAnimationFrame(() => autoScrollTickRef.current());
    if (navigator.vibrate) navigator.vibrate(20);
  }
  function movePointer(e: React.PointerEvent) {
    if (!ptrRef.current || e.pointerId !== ptrRef.current.pointerId) return;
    e.preventDefault();
    ptrPointRef.current = { x: e.clientX, y: e.clientY };
    setPtrDrag(d => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    showDropAt(e.clientX, e.clientY);
  }
  function endPointer(e: React.PointerEvent, drop: boolean) {
    const cur = ptrRef.current;
    if (!cur || e.pointerId !== cur.pointerId) return;
    ptrRef.current = null; ptrPointRef.current = null;
    if (autoScrollRef.current) { cancelAnimationFrame(autoScrollRef.current); autoScrollRef.current = null; }
    ptrCleanupRef.current?.(); ptrCleanupRef.current = null;
    try { cur.el.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    setPtrDrag(null); setDraggingJob(null); setDragOverDate(null); setDragOverRoute(null); setDragOverLoose(false);
    if (!drop) return;
    const t = dropTargetAt(e.clientX, e.clientY);
    if (!t) return;
    if (t.kind === 'route' && t.routeId) dropOnRoute(cur.job, t.date, t.routeId);
    else if (t.kind === 'day' && t.loose) dropOnDay(cur.job, String(cur.job.scheduled_date || '').slice(0, 10), null, true);
    else if (t.kind === 'day') dropOnDay(cur.job, t.date, t.timed ? e.clientY - t.el.getBoundingClientRect().top : null);
  }
  /** The grip that lifts a job under a finger (or a mouse). Not a component: the node must survive every re-render of a drag. */
  const grip = (job: any, size = 'h-4 w-4') => (
    <span role="button" aria-label="Drag to a route or another day" title="Drag to a route or another day"
      onPointerDown={e => liftByPointer(e, job)} onPointerMove={movePointer}
      onPointerUp={e => endPointer(e, true)} onPointerCancel={e => endPointer(e, false)}
      onClick={e => e.stopPropagation()}
      className="flex items-center self-stretch px-1.5 text-gray-400 cursor-grab active:cursor-grabbing select-none"
      style={{ touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}>
      <GripVertical className={size} />
    </span>
  );

  const periodLabel = viewMode === 'month'
    ? selectedDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : viewMode === 'week'
    ? `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${addDays(weekStart, 6).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Calendar</h1>
        <div className="flex-1" />
        <button onClick={goToday} className="btn-secondary text-xs px-3 py-1.5">Today</button>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          <button onClick={() => setViewMode('month')} className={`press px-3 py-1.5 text-xs font-medium ${viewMode === 'month' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Month</button>
          <button onClick={() => setViewMode('week')} className={`press px-3 py-1.5 text-xs font-medium border-l border-gray-300 ${viewMode === 'week' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Week</button>
          <button onClick={() => setViewMode('day')}  className={`press px-3 py-1.5 text-xs font-medium border-l border-gray-300 ${viewMode === 'day'  ? 'bg-brand-600 text-black' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Day</button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prevPeriod} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm font-medium text-gray-700 min-w-[200px] text-center">{periodLabel}</span>
          <button onClick={nextPeriod} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <button onClick={() => setPhoneFeed(true)} className="btn-secondary text-xs" title="Put these on your phone's calendar">
          <CalendarPlus className="h-3.5 w-3.5 mr-1" /> On my phone
        </button>
        <button onClick={() => setTodoModal({ date: isoDate(selectedDay) })} className="btn-secondary text-xs">
          ☐ To-do
        </button>
        <button onClick={() => setQuoteModal({ date: isoDate(selectedDay) })} className="btn-secondary text-xs">
          <Plus className="h-3.5 w-3.5 mr-1" /> Quote visit
        </button>
        <button onClick={() => setRouteDay(isoDate(selectedDay))} className="btn-secondary text-xs"
          title="Put the jobs that aren't on a route this day onto one, in driving order">
          <RouteIcon className="h-3.5 w-3.5 mr-1" /> Route loose jobs
        </button>
        <button onClick={() => setJobModal({ date: isoDate(selectedDay) })} className="btn-primary text-xs">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Job
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        {([
          ['showMowing',     'Mowing',          CAT.mowing.chip],
          ['showQuotes',     'Quotes',          CAT.quote.chip],
          ['showProjects',   'Projects',        CAT.project.chip],
          ['showTodos',      'To-dos',          CAT.todo.chip],
          ['showOpenShifts', 'Open Shifts',     'bg-amber-100 text-amber-700 border-amber-300'],
          ['showHolds',      'Holds',           HOLD.chip],
          ['showComplete',   'Completed',       'bg-gray-200 text-gray-700 border-gray-300'],
          ['showSkipped',    'Skipped/Canceled','bg-gray-100 text-gray-600 border-gray-300'],
        ] as const).map(([key, label, activeClass]) => (
          <button key={key} type="button"
            onClick={() => setFilters(f => ({ ...f, [key]: !f[key] }))}
            className={`px-2.5 py-1 rounded-full border font-medium transition-colors ${filters[key] ? activeClass : 'bg-white text-gray-500 border-gray-200'}`}>
            {label}
          </button>
        ))}
        {selectedJobId && (
          <span className="ml-auto text-gray-500 italic">Job selected — ⌘C copy · ⌘X cut · Del delete · Esc deselect</span>
        )}
        {clipboard && (
          <span className="ml-auto text-brand-600 font-medium">📋 Job in clipboard — click date then ⌘V to paste</span>
        )}
      </div>

      {/* Legend — colour is the KIND of work. Mowing = route stops (one block
          per route, tap to open), Quotes = in-person visits, Projects =
          everything else. Written bids are documents, not events. */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-gray-600">
        <span className="font-semibold text-gray-500 uppercase tracking-wide text-[10px]">Colors</span>
        {(['mowing', 'quote', 'project', 'todo'] as const).map(k => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm border-l-[3px]" style={{ backgroundColor: CAT[k].hex + '30', borderLeftColor: CAT[k].hex }} />
            {CAT[k].label}{k === 'mowing' ? ' (routes — tap to open)' : k === 'quote' ? ' (in-person visits)' : ''}
          </span>
        ))}
        <span className="text-gray-300">|</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-gray-100 border-l-[3px] border-gray-400" /> complete</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-yellow-100 border-l-[3px] border-yellow-400" /> in progress</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-gray-50 border-l-[3px] border-gray-300" /> skipped</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border-l-[3px] border-amber-400" /> open shift</span>
        <span className="inline-flex items-center gap-1.5" title="A bid with a start day on it. Nothing is booked until they sign — but if they accept, that spot is theirs.">
          <span className="inline-block w-3 h-3 rounded-sm bg-yellow-100 border border-dashed border-yellow-500" /> Yellow = a bid's start day, taken if they accept
        </span>
        <button type="button" onClick={copyOpenShiftLink} className="inline-flex items-center gap-1 font-semibold text-amber-800 underline hover:text-amber-950">
          <Copy className="h-3 w-3" /> Copy open-shift link
        </button>
      </div>

      {/* Month view — the iPhone Calendar shape: a grid of days with a dot per
          job (route colour), tap a day and its list sits underneath. */}
      {viewMode === 'month' && (() => {
        const todayIso = isoDate(new Date());
        const selIso = isoDate(selectedDay);
        // The picked days, first to last — one day unless a drag picked more.
        const [lo, hi] = rangeEnd && isoDate(rangeEnd) !== selIso
          ? (rangeEnd < selectedDay ? [rangeEnd, selectedDay] : [selectedDay, rangeEnd]) : [selectedDay, selectedDay];
        const loIso = isoDate(lo), hiIso = isoDate(hi);
        const pickedDays: Date[] = [];
        for (let d = new Date(lo.getFullYear(), lo.getMonth(), lo.getDate()); isoDate(d) <= hiIso; d = addDays(d, 1)) pickedDays.push(d);
        const multi = pickedDays.length > 1;
        const sortedJobs = (iso: string) => jobsForDay(iso).slice().sort((a: any, b: any) => String(a.time_start || '99').localeCompare(String(b.time_start || '99')));
        const dayJobs = sortedJobs(selIso);
        const dayHolds = holdsForDay(selIso);
        const rangeCount = pickedDays.reduce((n, d) => n + jobsForDay(isoDate(d)).length, 0);
        const rangeHolds = pickedDays.reduce((n, d) => n + holdsForDay(isoDate(d)).length, 0);
        const countWords = (jobsN: number, holdsN: number) => {
          const parts = [jobsN ? `${jobsN} job${jobsN === 1 ? '' : 's'}` : '', holdsN ? holdsWord(holdsN) : ''].filter(Boolean);
          return parts.length ? parts.join(' · ') : 'nothing scheduled';
        };
        const isoAt = (x: number, y: number) => (document.elementFromPoint(x, y) as HTMLElement | null)?.closest('[data-iso]')?.getAttribute('data-iso') || null;
        const dayOf = (iso: string) => new Date(iso + 'T12:00:00');
        const dotColor = (j: any) => j.status === 'skipped' ? '#d1d5db' : j.status === 'complete' ? '#9ca3af' : isWinterHold(j) ? WINTER.hex : CAT[categoryOf(j)].hex;
        const fmtTime = (t: string | null | undefined) => {
          if (!hasRealTime(t)) return 'Anytime';
          const [h, m] = String(t).split(':').map(Number);
          return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
        };
        return (
          <div className="card overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-200 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className="py-1.5">{d}</div>)}
            </div>
            {/* Tap a day, or press and drag across days to pick several. Touch keeps up/down for scrolling the page,
                so on a phone the drag runs along a week; a mouse drags any way. */}
            <div className="grid grid-cols-7 select-none" style={{ touchAction: 'pan-y' }}
              onPointerDown={e => {
                const iso = isoAt(e.clientX, e.clientY); if (!iso) return;
                dragRef.current = { from: dayOf(iso), moved: false };
                setSelectedDay(dayOf(iso)); setRangeEnd(null);
              }}
              onPointerMove={e => {
                const drag = dragRef.current; if (!drag || (e.pointerType === 'mouse' && e.buttons !== 1)) return;
                const iso = isoAt(e.clientX, e.clientY);
                if (!iso || iso === isoDate(rangeEnd || drag.from)) return;
                drag.moved = true; setRangeEnd(dayOf(iso));
              }}
              onPointerUp={() => { dragRef.current = null; }}
              onPointerCancel={() => { dragRef.current = null; }}
              onPointerLeave={e => { if (e.pointerType === 'mouse') dragRef.current = null; }}>
              {gridDays.map((d, i) => {
                const iso = isoDate(d);
                const inMonth = d.getMonth() === selectedDay.getMonth();
                const js = jobsForDay(iso);
                const hs = holdsForDay(iso);
                const inRange = multi && iso >= loIso && iso <= hiIso;
                const isSel = multi ? inRange : iso === selIso;
                const isToday = iso === todayIso;
                return (
                  <button key={iso} type="button" data-iso={iso} data-drop="day" data-date={iso}
                    className={`h-14 sm:h-16 flex flex-col items-center justify-start pt-1 border-b border-r border-gray-100 ${i % 7 === 6 ? 'border-r-0' : ''} ${inRange ? 'bg-brand-50' : inMonth ? '' : 'bg-gray-50/60'} ${ptrDrag && dragOverDate === iso ? 'ring-2 ring-inset ring-brand-500 bg-brand-50' : ''}`}>
                    <span className={`h-7 w-7 flex items-center justify-center rounded-full text-sm ${
                      isSel ? 'bg-brand-600 text-black font-semibold' : isToday ? 'text-brand-600 font-bold' : inMonth ? 'text-gray-800' : 'text-gray-300'}`}>
                      {d.getDate()}
                    </span>
                    <span className="flex items-center gap-0.5 mt-0.5 h-2">
                      {js.slice(0, 4).map((j: any) => <span key={j.id} className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColor(j) }} />)}
                      {js.length > 4 && <span className="text-[9px] leading-none text-gray-500">+{js.length - 4}</span>}
                      {hs.length > 0 && js.length <= 4 && <span className="inline-block w-1.5 h-1.5 rounded-full border border-dashed" style={{ borderColor: HOLD.hex, backgroundColor: HOLD.hex + '60' }} title={holdsWord(hs.length)} />}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="border-t border-gray-200">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
                <p className="text-sm font-semibold text-gray-800">
                  {multi
                    ? `${lo.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} – ${hi.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
                    : selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {multi ? `${pickedDays.length} days · ${countWords(rangeCount, rangeHolds)}` : countWords(dayJobs.length, dayHolds.length)}
                  </span>
                  {multi && <button onClick={() => setRangeEnd(null)} className="ml-2 text-xs font-medium text-brand-600">Clear</button>}
                </p>
                <span className="flex items-center gap-3">
                  <button onClick={() => setTodoModal({ date: selIso })} className="text-xs font-medium text-slate-600">☐ To-do</button>
                  <button onClick={() => setJobModal({ date: selIso })} className="text-xs font-medium text-brand-600 flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
                </span>
              </div>
              {pickedDays.map(pd => {
                const selIso = isoDate(pd);
                const dayJobs = sortedJobs(selIso);
                const dayHolds = holdsForDay(selIso);
                return (
                  <div key={selIso}>
                    {multi && (
                      <p className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-600 uppercase tracking-wide border-t border-gray-100">
                        {pd.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                        <span className="ml-2 font-normal normal-case text-gray-500">{countWords(dayJobs.length, dayHolds.length)}</span>
                      </p>
                    )}
              {dayJobs.length > 0 && (() => {
                // Route stops fold into one row per route (tap to open); quote
                // visits and projects stay as their own rows.
                const rows: any[] = [];
                const groups = new Map<number, any>();
                for (const j of dayJobs) {
                  if (categoryOf(j) === 'mowing' && j.route_id) {
                    let g = groups.get(j.route_id);
                    if (!g) { g = { route: true, route_id: j.route_id, name: routeInfoMap[j.route_id]?.name || `Route ${j.route_id}`, stops: [] as any[] }; groups.set(j.route_id, g); rows.push(g); }
                    g.stops.push(j);
                  } else rows.push(j);
                }
                // A route's stops in the Routes page order, not the clock (services/routeOrder).
                groups.forEach(g => g.stops.sort((a: any, b: any) => (a.route_seq ?? 9999) - (b.route_seq ?? 9999)));
                const jobRow = (j: any) => (
                  <li key={j.id} data-job={j.id} className={`flex items-stretch ${ptrDrag?.job?.id === j.id ? 'opacity-40' : ''}`}>
                    <button type="button" onClick={() => setViewJobId(j.id)} className="min-w-0 flex-1 text-left px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50">
                      <span className="w-1 self-stretch rounded-full" style={{ backgroundColor: dotColor(j) }} />
                      <span className="w-[76px] flex-shrink-0 text-xs text-gray-500">{fmtTime(j.time_start)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-900 truncate">{categoryOf(j) === 'todo' ? (j.title || 'To-do') : (j.customer_name || j.title || 'Job')}</span>
                        <span className="block text-xs text-gray-500 truncate">{categoryOf(j) === 'todo' ? ['To-do', j.todo_customer_name].filter(Boolean).join(' · ') : categoryOf(j) === 'quote' ? 'Quote visit' : formatServiceType(j.service_type)}{j.address ? ` · ${String(j.address).split(',')[0]}` : ''}{j.status === 'complete' ? ' · done' : j.status === 'in_progress' ? ' · in progress' : isWinterHold(j) ? ` · ${WINTER.words}` : ''}</span>
                      </span>
                    </button>
                    {/* Press and drag: onto a route row below or above, or onto a day in the grid. */}
                    {canLift(j) && grip(j)}
                  </li>
                );
                return (
                  <ul className="divide-y divide-gray-100">
                    {rows.map((r: any) => {
                      if (!r.route) return jobRow(r);
                      const key = `${selIso}:${r.route_id}`;
                      const open = expandedRoutes.has(key);
                      const mins = r.stops.reduce((sum: number, j: any) => sum + (j.estimated_minutes_snapshot || 30), 0);
                      const done = r.stops.filter((j: any) => j.status === 'complete').length;
                      return (
                        <li key={`r${r.route_id}`} data-drop="route" data-date={selIso} data-route={r.route_id}
                          className={ptrDrag && dragOverRoute === key ? 'ring-2 ring-inset ring-green-600 bg-green-50' : ''}>
                          {ptrDrag && dragOverRoute === key && (
                            <p className="px-4 pt-1 text-[11px] font-bold text-green-800">Drop to add to {r.name}</p>
                          )}
                          <button type="button" onClick={() => toggleRoute(key)} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50">
                            <span className="w-1 self-stretch rounded-full" style={{ backgroundColor: CAT.mowing.hex }} />
                            <span className="w-[76px] flex-shrink-0 text-xs text-gray-500">{fmtTime(routeInfoMap[r.route_id]?.planned_start_time)}</span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-semibold text-gray-900 truncate">{r.name}</span>
                              <span className="block text-xs text-gray-500 truncate">{r.stops.length} stops · ~{Math.round(mins / 6) / 10} h{done ? ` · ${done} done` : ''}</span>
                            </span>
                            <ChevronDown className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                          </button>
                          {open && <ul className="divide-y divide-gray-50 bg-gray-50/70 pl-3">{r.stops.map((j: any) => jobRow(j))}</ul>}
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
              {dayHolds.length > 0 && (
                <ul className="divide-y divide-yellow-100 border-t border-dashed border-yellow-300 bg-yellow-50/60">
                  {dayHolds.map(h => (
                    <li key={`hold-${h.quote_id}`}>
                      <button type="button" onClick={() => openHold(h)} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-yellow-100/70"
                        title="A bid's start day — taken if they accept. Tap to open the bid.">
                        <span className="w-1 self-stretch rounded-full border border-dashed" style={{ borderColor: HOLD.hex, backgroundColor: HOLD.hex + '60' }} />
                        <span className="w-[76px] flex-shrink-0 text-xs text-yellow-800">{fmtTime(h.time_start)}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-yellow-900 truncate">{h.title}</span>
                          <span className="block text-xs text-yellow-800/80 truncate">{h.kind_words} · if they accept{h.minutes ? ` · ${h.minutes}m` : ''}</span>
                        </span>
                        <span className="text-[9px] font-bold tracking-wide text-yellow-800 border border-dashed border-yellow-500 rounded px-1 py-0.5 flex-shrink-0">HOLD</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Calendar grid */}
      <div className={`card overflow-hidden ${viewMode === 'month' ? 'hidden' : ''}`}>
        {/* Day headers */}
        <div className={`grid border-b border-gray-200 ${viewMode === 'week' ? 'grid-cols-[60px_repeat(7,1fr)]' : 'grid-cols-[60px_1fr]'}`}>
          <div className="border-r border-gray-200" />
          {displayDays.map(d => {
            const dateStr = isoDate(d);
            const isToday = dateStr === isoDate(new Date());
            const dayJobsList = jobsForDay(dateStr);
            const dayRevenue = dayJobsList.reduce((s: number, j: any) => s + (j.price_snapshot || 0), 0);
            const dayLabor = dayJobsList.reduce((s: number, j: any) => s + (j.crew_pay_is_manual ? (j.crew_pay_override || 0) : (j.estimated_crew_pay || 0)), 0);
            const dayHoldsN = holdsForDay(dateStr).length;   // never in the day's money or hours: a hold is not work
            return (
              <div key={dateStr}
                onClick={() => { if (viewMode === 'week') { setSelectedDay(d); setViewMode('day'); } }}
                className={`px-1 py-2 text-center border-r border-gray-200 last:border-r-0 cursor-pointer hover:bg-gray-50 ${viewMode === 'week' ? 'select-none' : ''}`}>
                <div className="text-xs text-gray-500 uppercase">
                  {d.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className={`text-sm font-semibold mt-0.5 w-7 h-7 rounded-full flex items-center justify-center mx-auto ${isToday ? 'bg-brand-600 text-black' : 'text-gray-900'}`}>
                  {d.getDate()}
                </div>
                {dayRevenue > 0 && (
                  <div className="mt-1 space-y-0.5">
                    <p className="text-[9px] text-green-700 font-semibold">${dayRevenue.toFixed(0)} rev</p>
                    {dayLabor > 0 && <p className="text-[9px] text-gray-500">${dayLabor.toFixed(0)} labor</p>}
                  </div>
                )}
                {dayHoldsN > 0 && <p className="mt-0.5 text-[9px] font-semibold text-yellow-700" title="Bids with a start day here — taken if they accept">{holdsWord(dayHoldsN)}</p>}
              </div>
            );
          })}
        </div>

        {/* Time slots + events */}
        <div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
          <div className={`grid ${viewMode === 'week' ? 'grid-cols-[60px_repeat(7,1fr)]' : 'grid-cols-[60px_1fr]'}`}
            style={{ height: TOTAL_HOURS * HOUR_HEIGHT }}>

            {/* Time column */}
            <div className="relative border-r border-gray-200">
              {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                <div key={i} className="absolute w-full border-t border-gray-100 flex items-start justify-end pr-2"
                  style={{ top: i * HOUR_HEIGHT, height: HOUR_HEIGHT }}>
                  <span className="text-xs text-gray-500 -mt-2">
                    {((START_HOUR + i) % 12 || 12)}{START_HOUR + i < 12 ? 'am' : 'pm'}
                  </span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {displayDays.map(d => {
              const dateStr = isoDate(d);
              const dayJobs = jobsForDay(dateStr);
              const dayHolds = holdsForDay(dateStr);
              const isToday = dateStr === isoDate(new Date());
              const isDragOver = dragOverDate === dateStr;

              return (
                <div key={dateStr} data-drop="day" data-date={dateStr} data-timed="1"
                  className={`relative border-r border-gray-200 last:border-r-0 transition-colors ${isToday ? 'bg-brand-50/30' : ''} ${isDragOver ? 'bg-brand-50/50' : ''} ${viewMode === 'week' ? 'cursor-zoom-in' : 'cursor-cell'}`}
                  onClick={e => handleSlotClick(dateStr, e)}
                  onDragOver={e => handleDragOver(e, dateStr)}
                  onDrop={e => handleDrop(e, dateStr)}
                  onDragLeave={() => setDragOverDate(null)}
                >

                  {/* Hour grid lines */}
                  {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                    <div key={i} className="absolute w-full border-t border-gray-100"
                      style={{ top: i * HOUR_HEIGHT, height: HOUR_HEIGHT }} />
                  ))}

                  {/* Half-hour lines */}
                  {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                    <div key={`h${i}`} className="absolute w-full border-t border-gray-50"
                      style={{ top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2 }} />
                  ))}

                  {/* Route blocks + individual events */}
                  {(() => {
                    // ── Split jobs: grouped by route vs individual ──────────────
                    const routeGroups = new Map<number, any[]>();
                    const unroutedJobs: any[] = [];
                    for (const job of dayJobs) {
                      if (job.route_id && !job.is_open_shift) {
                        if (!routeGroups.has(job.route_id)) routeGroups.set(job.route_id, []);
                        routeGroups.get(job.route_id)!.push(job);
                      } else {
                        unroutedJobs.push(job);
                      }
                    }
                    // Sort each route's stops by position
                    routeGroups.forEach(rjobs => {
                      rjobs.sort((a: any, b: any) => (a.route_seq ?? 9999) - (b.route_seq ?? 9999));   // the Routes page order (services/routeOrder)
                    });

                    // ── Route blocks ────────────────────────────────────────────
                    const routeBlocks = Array.from(routeGroups.entries()).map(([routeId, rjobs]) => {
                      if (!rjobs.length) return null;
                      const info = routeInfoMap[routeId];
                      const startTime = info?.planned_start_time || '08:00';
                      const topPx    = minutesToPx(timeToMinutes(startTime));
                      const totalMins = rjobs.reduce((s: number, j: any) => s + (j.estimated_minutes_snapshot || 30), 0);
                      const lineH    = viewMode === 'week' ? 14 : 19;
                      const headerH  = viewMode === 'week' ? 20 : 24;
                      const rKey     = `${isoDate(d)}:${routeId}`;
                      const open     = expandedRoutes.has(rKey);
                      const minH     = headerH + (open ? lineH * rjobs.length : 16) + 4;
                      const heightPx = Math.max(minH, durationToPx(totalMins));
                      const hex      = CAT.mowing.hex;

                      const [hh = 8, mm = 0] = startTime.split(':').map(Number);
                      const timeFmt = `${hh % 12 || 12}:${String(mm).padStart(2, '0')}${hh < 12 ? 'am' : 'pm'}`;

                      const allDone   = rjobs.every((j: any) => j.status === 'complete');
                      const inProg    = rjobs.some((j: any)  => j.status === 'in_progress');

                      return (
                        <div
                          key={`route-${routeId}`}
                          data-drop="route" data-date={isoDate(d)} data-route={routeId}
                          onClick={e => {
                            e.stopPropagation();
                            if (viewMode === 'week') { setSelectedDay(d); setViewMode('day'); }
                          }}
                          // Drop a job here and it joins this route (best spot in the order).
                          onDragOver={e => {
                            if (!draggingJob) return;
                            e.preventDefault(); e.stopPropagation();
                            e.dataTransfer.dropEffect = 'move';
                            if (dragOverRoute !== rKey) setDragOverRoute(rKey);
                            if (dragOverDate) setDragOverDate(null);
                          }}
                          onDragLeave={e => { e.stopPropagation(); if (dragOverRoute === rKey) setDragOverRoute(null); }}
                          onDrop={e => handleRouteDrop(e, isoDate(d), routeId)}
                          className={`absolute rounded-sm border-l-[3px] overflow-hidden select-none ${dragOverRoute === rKey ? 'ring-2 ring-green-600 z-20' : ''}`}
                          style={{
                            top: topPx, height: heightPx,
                            left: '1px', right: '1px',
                            backgroundColor: dragOverRoute === rKey ? hex + '40' : hex + '18',
                            borderLeftColor: hex,
                            cursor: viewMode === 'week' ? 'zoom-in' : 'default',
                          }}
                        >
                          {dragOverRoute === rKey && (
                            <div className="absolute inset-x-0 bottom-0 px-1 py-0.5 text-[9px] font-bold text-white bg-green-700/90 z-10">
                              Drop to add to {info?.name || `Route ${routeId}`}
                            </div>
                          )}
                          {/* Header row */}
                          <div
                            onClick={e => { if (viewMode === 'day') { e.stopPropagation(); toggleRoute(rKey); } }}
                            className={`flex items-center gap-1 px-1 border-b border-black/10 ${viewMode === 'day' ? 'cursor-pointer' : ''}`}
                            style={{ height: headerH, backgroundColor: hex + '30' }}
                            title={viewMode === 'day' ? (open ? 'Hide the stops' : 'Show the stops') : undefined}
                          >
                            <span
                              className="text-[10px] font-bold leading-none truncate flex-1"
                              style={{ color: hex }}
                            >
                              {info?.name || `Route ${routeId}`}
                            </span>
                            <span className="text-[9px] font-medium text-gray-500 flex-shrink-0 leading-none">
                              {timeFmt}
                            </span>
                            {allDone  && <span className="text-[8px] text-green-600 font-bold flex-shrink-0">✓</span>}
                            {inProg   && <span className="text-[8px] text-yellow-600 font-bold flex-shrink-0">▶</span>}
                            <span className="text-[9px] text-gray-500 flex-shrink-0 leading-none">{rjobs.length}{open ? ' ▾' : ' ▸'}</span>
                          </div>

                          {/* Stop list — only when the route is opened */}
                          {open && rjobs.map((job: any, i: number) => (
                            <div
                              key={job.id} data-job={job.id}
                              draggable={viewMode === 'day' && mouseDrag}
                              onDragStart={e => { if (ptrRef.current) { e.preventDefault(); return; } e.stopPropagation(); handleDragStart(e, job); }}
                              onDragEnd={handleDragEnd}
                              onClick={e => {
                                e.stopPropagation();
                                if (viewMode === 'week') { setSelectedDay(d); setViewMode('day'); return; }
                                setSelectedJobId(job.id);
                                setPasteTargetDate(job.scheduled_date);
                                setViewJobId(job.id);
                              }}
                              className={`flex items-center gap-0.5 px-1 hover:bg-black/5 transition-colors
                                ${job.status === 'complete'    ? 'opacity-40' : ''}
                                ${job.status === 'skipped'     ? 'opacity-25 line-through' : ''}
                                ${job.status === 'in_progress' ? 'font-semibold' : ''}
                                ${selectedJobId === job.id     ? 'bg-brand-100/60' : ''}
                              `}
                              style={{ height: lineH }}
                            >
                              <span className="text-[8px] text-gray-500 flex-shrink-0 w-3.5 leading-none">{i + 1}.</span>
                              <span className="text-[9px] leading-tight truncate text-gray-700">
                                {job.customer_name}{Number(job.span_days) > 1 ? ` (D${job.span_day}/${job.span_days})` : ''}<AccountMark mark={job.account_mark} />
                                {viewMode === 'day' && job.address
                                  ? <span className="opacity-50"> · {job.address.split(',')[0]}</span>
                                  : null}
                              </span>
                              {viewMode === 'day' && job.estimated_minutes_snapshot
                                ? <span className="text-[8px] text-gray-500 ml-auto pl-1 flex-shrink-0">{job.estimated_minutes_snapshot}m</span>
                                : null}
                              {viewMode === 'day' && canLift(job) && grip(job, 'h-3.5 w-3.5')}
                            </div>
                          ))}

                          {/* Summary line: always when folded, and as the day-view footer */}
                          {(viewMode === 'day' || !open) && (
                            <div className="px-1 py-0.5 border-t border-black/10 flex items-center gap-2 mt-auto">
                              <span className="text-[8px] text-gray-500">
                                {rjobs.length} stops · ~{Math.round(totalMins / 60 * 10) / 10}h{!open && viewMode === 'day' ? ' · tap the name to open' : ''}
                              </span>
                              {allDone && <span className="text-[8px] text-green-600 font-semibold">All complete</span>}
                            </div>
                          )}
                        </div>
                      );
                    });

                    // ── Individual (unrouted / open-shift) events ───────────────
                    const maxCols = viewMode === 'week' ? 3 : 8;
                    // Holds share the columns with the loose jobs so a hold and a visit at the same hour sit side by
                    // side. Their ids are negative so they never collide with a job's.
                    const holdEvents = dayHolds.map(h => ({ id: -h.quote_id, time_start: h.time_start, estimated_minutes_snapshot: h.minutes, hold: h }));
                    const eventLayout = computeEventLayout([...unroutedJobs, ...holdEvents], maxCols);
                    const hiddenBySlot = new Map<string, number>();

                    // ── Holds: yellow, dashed, not a job ────────────────────────
                    const holdBlocks = holdEvents.map(ev => {
                      const h = ev.hold;
                      const info = eventLayout.get(ev.id) ?? { leftPct: 0, widthPct: 100 };
                      const hasTime = hasRealTime(h.time_start);
                      const top = hasTime ? minutesToPx(timeToMinutes(h.time_start as string)) : 4;
                      if (info.hidden) {
                        const key = `${top}_0`;
                        hiddenBySlot.set(key, (hiddenBySlot.get(key) || 0) + 1);
                        return null;
                      }
                      const height = hasTime ? Math.max(durationToPx(h.minutes), 48) : 48;
                      return (
                        <div
                          key={`hold-${h.quote_id}`} data-hold={h.quote_id}
                          onClick={e => { e.stopPropagation(); if (draggingJob || ptrDrag) return; openHold(h); }}
                          title={`${h.title} — ${h.kind_words}. Taken if they accept. Tap to open the bid.`}
                          className={`absolute rounded border border-dashed border-l-2 px-1 py-0.5 cursor-pointer overflow-hidden hover:opacity-90 ${HOLD.card}`}
                          style={{
                            top, height,
                            left: `calc(${info.leftPct}% + 1px)`,
                            width: `calc(${info.widthPct}% - 2px)`,
                            backgroundImage: 'repeating-linear-gradient(135deg, rgba(234,179,8,0.10) 0 6px, transparent 6px 12px)',
                          }}>
                          <p className="text-[10px] font-bold leading-tight truncate">🟡 {h.title}</p>
                          <p className="text-[9px] opacity-80 truncate leading-tight">{h.kind_words} · if they accept</p>
                          {hasTime && (
                            <p className="text-[9px] opacity-70 leading-tight">
                              {fmtClock(h.time_start as string)}{h.minutes ? ` · ${h.minutes}m` : ''}
                            </p>
                          )}
                          <p className="text-[9px] font-bold opacity-80">HOLD</p>
                        </div>
                      );
                    });

                    const unroutedBlocks = unroutedJobs.map((job: any) => {
                      const info = eventLayout.get(job.id) ?? { leftPct: 0, widthPct: 100 };
                      if (info.hidden) {
                        const top = hasRealTime(job.time_start) ? minutesToPx(timeToMinutes(job.time_start)) : 4;
                        const key = `${top}_0`;
                        hiddenBySlot.set(key, (hiddenBySlot.get(key) || 0) + 1);
                        return null;
                      }
                      const hasTime = hasRealTime(job.time_start);
                      const top     = hasTime ? minutesToPx(timeToMinutes(job.time_start)) : 4;
                      const height  = hasTime ? Math.max(durationToPx(job.estimated_minutes_snapshot), 48) : 48;
                      const isSelected = selectedJobId === job.id;
                      const isDragging = draggingJob?.id === job.id;
                      const colorClass =
                          job.status === 'complete'    ? 'bg-gray-100 border-gray-400 text-gray-600'
                        : job.status === 'skipped'     ? 'bg-gray-50 border-gray-300 text-gray-500'
                        : job.status === 'in_progress' ? 'bg-yellow-100 border-yellow-400 text-yellow-900'
                        : job.is_open_shift             ? 'bg-amber-100 border-amber-400 text-amber-900'
                        : isWinterHold(job)             ? WINTER.card
                        :                                 CAT[categoryOf(job)].card;
                      const { leftPct, widthPct } = info;
                      const streetAddr   = job.address?.split(',')[0] || '';
                      const serviceLabel = categoryOf(job) === 'quote' ? 'Quote visit' : formatServiceType(job.service_type || job.title);
                      const dayTag       = Number(job.span_days) > 1 ? ` · Day ${job.span_day}/${job.span_days}` : '';
                      const cardTitle    = categoryOf(job) === 'todo' ? `☐ ${job.title || 'To-do'}${job.todo_customer_name ? ` · ${job.todo_customer_name}` : ''}` : `${job.customer_name} — ${serviceLabel}${dayTag}`;
                      return (
                        <div
                          key={job.id} data-job={job.id}
                          draggable={mouseDrag}
                          onDragStart={e => { if (ptrRef.current) { e.preventDefault(); return; } e.stopPropagation(); handleDragStart(e, job); }}
                          onDragEnd={handleDragEnd}
                          onClick={e => {
                            e.stopPropagation();
                            if (draggingJob) return;
                            setSelectedJobId(job.id);
                            setPasteTargetDate(job.scheduled_date);
                            setViewJobId(job.id);
                          }}
                          className={`absolute rounded border-l-2 px-1 py-0.5 cursor-grab active:cursor-grabbing overflow-hidden transition-all ${colorClass} ${isDragging ? 'opacity-40' : 'hover:opacity-90'} ${isSelected ? 'ring-2 ring-offset-1 ring-brand-600 z-10' : ''}`}
                          style={{
                            top, height,
                            left: `calc(${leftPct}% + 1px)`,
                            width: `calc(${widthPct}% - 2px)`,
                          }}>
                          <p className="text-[10px] font-bold leading-tight truncate">{cardTitle}</p>
                          {streetAddr && <p className="text-[9px] opacity-75 truncate leading-tight">{streetAddr}</p>}
                          {hasRealTime(job.time_start) && (
                            <p className="text-[9px] opacity-70 leading-tight">
                              {fmtClock(job.time_start)}{job.estimated_minutes_snapshot ? ` · ${job.estimated_minutes_snapshot}m` : ''}
                            </p>
                          )}
                          {job.price_snapshot > 0 && <p className="text-[9px] opacity-75 font-semibold">${job.price_snapshot}</p>}
                          {job.is_open_shift && <p className="text-[9px] font-bold opacity-80">OPEN SHIFT</p>}
                          {isWinterHold(job) && <p className="text-[9px] font-bold opacity-80" title={WINTER.words}>WINTER — DATE TO BE SET</p>}
                          {viewMode === 'day' && canLift(job) && (
                            <span className="absolute right-0 inset-y-0 flex items-center bg-white/50">{grip(job)}</span>
                          )}
                        </div>
                      );
                    });

                    // "+N more" chips for hidden events
                    const overflowChips = Array.from(hiddenBySlot.entries()).map(([slotKey, count]) => {
                      const top = parseInt(slotKey.split('_')[0], 10);
                      return (
                        <div
                          key={`overflow-${slotKey}`}
                          onClick={e => { e.stopPropagation(); if (viewMode === 'week') { setSelectedDay(d); setViewMode('day'); } }}
                          className="press absolute right-0.5 text-[9px] font-semibold bg-brand-600 text-black rounded px-1.5 py-0.5 z-20 cursor-pointer hover:bg-brand-500"
                          style={{ top: top + 2 }}>
                          +{count} more
                        </div>
                      );
                    });

                    return [...routeBlocks.filter(Boolean), ...holdBlocks.filter(Boolean), ...unroutedBlocks.filter(Boolean), ...overflowChips];
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Under a finger drag of a route stop, a bar to let go on: the stop comes off its route for this one day and
          stays put otherwise (owner, Oct 29 2026: "I need to be able to get Mimi out of the route … on the phone").
          Fixed above the bottom nav, so it is in reach however long the route block is; the drop is the same /move
          a drop on the hour column makes, without a time. The series is untouched: next week she is back on. */}
      {ptrDrag && ptrDrag.job.route_id && !ptrDrag.job.is_open_shift && (
        <div data-drop="day" data-date={String(ptrDrag.job.scheduled_date || '').slice(0, 10)} data-loose="1"
          className={`fixed inset-x-3 z-[65] rounded-2xl border-2 border-dashed px-4 py-3 text-center shadow-lg transition-colors ${dragOverLoose ? 'border-red-600 bg-red-50' : 'border-gray-400 bg-white/95'}`}
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)' }}>
          <p className={`text-sm font-semibold ${dragOverLoose ? 'text-red-800' : 'text-gray-800'}`}>
            {dragOverLoose ? 'Let go: ' : 'Drag here: '}{ptrDrag.job.customer_name || ptrDrag.job.title || 'this job'} off {routeInfoMap[ptrDrag.job.route_id]?.name || 'the route'}
          </p>
          <p className="text-[11px] text-gray-500">Stays on {shortDay(ptrDrag.job.scheduled_date)} as a job of its own — this visit only</p>
        </div>
      )}
      {/* What is being dragged by a finger, following it; the drop target lights up under it. */}
      {ptrDrag && (
        <div className="fixed z-[70] pointer-events-none px-2.5 py-1.5 rounded-lg shadow-lg bg-white border border-gray-300 text-xs text-gray-900"
          style={{ left: Math.min(ptrDrag.x + 14, window.innerWidth - 230), top: ptrDrag.y - (dragOverLoose ? 104 : 44), maxWidth: 220 }}>
          <span className="font-semibold">{ptrDrag.job.customer_name || ptrDrag.job.title || 'Job'}</span>
          <span className="block text-[10px] text-gray-500">{dragOverLoose ? 'Let go to take it off the route' : dragOverRoute ? 'Let go to put it on this route' : dragOverDate ? 'Let go to move it to this day' : ptrDrag.job.route_id && !ptrDrag.job.is_open_shift ? 'Drop on a route, a day, or the bar below' : 'Drop on a route or a day'}</span>
        </div>
      )}

      {/* Create Job modal */}
      {/* A to-do for a client at an address (components/TodoSheet, services/todos) — grey on the day, nobody billed. */}
      <TodoSheet date={todoModal?.date || ''} open={!!todoModal} onClose={() => setTodoModal(null)} onAdded={() => { setTodoModal(null); qc.invalidateQueries('calendar-jobs'); }} />
      <QuoteVisitSheet date={quoteModal?.date || ''} open={!!quoteModal} presetCustomerId={quoteModal?.customer_id} onClose={() => setQuoteModal(null)}
        onAdded={() => { setQuoteModal(null); qc.invalidateQueries('calendar-jobs'); }} />
      <PhoneFeedSheet open={phoneFeed} onClose={() => setPhoneFeed(false)} />
      {/* stay: a day just offered as one open shift keeps the sheet up to say what happened (and hold any texts to send). */}
      {routeDay && <RouteDaySheet date={routeDay} startOffer={routeDayOffer} onClose={() => { setRouteDay(null); setRouteDayOffer(false); }}
        onBuilt={(stay?: boolean) => { if (!stay) { setRouteDay(null); setRouteDayOffer(false); } qc.invalidateQueries('calendar-jobs'); qc.invalidateQueries('routes-for-calendar'); }} />}
      {jobModal && (
        <CreateJobModal
          date={jobModal.date}
          initialTime={jobModal.time || ''}
          preCustomerId={jobModal.prefill?.customer_id || preCustomer}
          prePropertyId={jobModal.prefill?.property_id || preProperty}
          prefill={jobModal.prefill}
          onClose={() => setJobModal(null)}
          onCreated={() => {
            if (jobModal?.cutJobId) {
              api.delete(`/jobs/${jobModal.cutJobId}`).then(() => qc.invalidateQueries('calendar-jobs'));
            } else {
              qc.invalidateQueries('calendar-jobs');
            }
            setJobModal(null);
          }}
        />
      )}

      {/* Recurring delete modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Delete Recurring Job</h3>
              <p className="text-sm text-gray-500 mt-0.5 truncate">{deleteTarget.customer_name} · {deleteTarget.scheduled_date}</p>
            </div>
            <div className="px-5 pb-5 pt-4 space-y-2">
              <button
                onClick={() => { deleteJobMutation.mutate({ id: deleteTarget.id }); }}
                disabled={readOnly || (deleteJobMutation.isLoading)}
                className="press w-full py-2.5 text-sm font-semibold text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">
                Delete this occurrence only
              </button>
              <button
                onClick={() => { deleteJobMutation.mutate({ id: deleteTarget.id, mode: 'this_and_future' }); }}
                disabled={readOnly || (deleteJobMutation.isLoading)}
                className="w-full py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                Delete this and all future occurrences
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteJobMutation.isLoading}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-600">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View job modal */}
      {viewJobId && (
        <JobPreviewModal
          jobId={viewJobId}
          onClose={() => setViewJobId(null)}
          onOpen={(job?: any) => { navigate((job?.service_type || '') === 'estimate' ? `/quote-visits/${viewJobId}` : `/jobs/${viewJobId}`); }}
          onRefresh={() => qc.invalidateQueries('calendar-jobs')}
          onDeleteRequest={(job: any) => { setViewJobId(null); requestDelete(job); }}
        onDuplicate={(job: any) => {
            setViewJobId(null);
            setJobModal({
              date: isoDate(new Date()),
              prefill: {
                customer_id: String(job.customer_id),
                property_id: String(job.property_id || ''),
                crew_id: String(job.crew_id || ''),
                assigned_crew_member_id: String(job.assigned_crew_member_id || ''),
                time_start: job.time_start || '',
                notes: job.notes || '',
                title: job.title || '',
                service_type: job.service_type || '',
              }
            });
          }}
        />
      )}
    </div>
  );
}


// ── Job preview pop-up (click on event) ──────────────────────────────────────
function JobPreviewModal({ jobId, onClose, onOpen, onRefresh, onDuplicate, onDeleteRequest }: {
  jobId: number; onClose: () => void; onOpen: (job?: any) => void; onRefresh: () => void;
  onDuplicate: (job: any) => void; onDeleteRequest: (job: any) => void;
}) {
  const { data, isLoading } = useQuery(['job-preview', jobId], () =>
    api.get(`/jobs/${jobId}`).then(r => r.data)
  );
  const job = data?.job;
  const effectivePay = job?.crew_pay_is_manual ? job?.crew_pay_override : job?.estimated_crew_pay;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : !job ? (
          <div className="p-8 text-center text-gray-500">Job not found</div>
        ) : (
          <>
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <p className="font-semibold text-gray-900">{job.customer_name}<AccountMark mark={job.account_mark} />{Number(job.span_days) > 1 ? <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">Day {job.span_day} of {job.span_days}</span> : null}</p>
                <p className="text-sm text-gray-500">{job.property_label ? `${job.property_label} · ` : ''}{job.address?.split(',')[0]}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  job.status === 'complete' ? 'bg-green-100 text-green-700' :
                  job.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                  job.status === 'skipped' ? 'bg-gray-100 text-gray-600' : 'bg-brand-100 text-brand-700'
                }`}>{job.status.replace('_', ' ')}</span>
                <button onClick={onClose}><X className="h-4 w-4 text-gray-500 hover:text-gray-600" /></button>
              </div>
            </div>
            <div className="px-5 py-4 space-y-2 text-sm text-gray-700">
              <div className="flex gap-2">
                <Clock className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <span>{new Date(job.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  {job.time_start ? ` · ETA ${fmtClock(job.time_start)}` : ''}
                  {job.estimated_minutes_snapshot ? ` · ${job.estimated_minutes_snapshot}min` : ''}
                </span>
              </div>
              {job.assigned_crew_member_name && <p className="text-gray-500">Assigned: {job.assigned_crew_member_name}</p>}
              {job.price_snapshot && job.service_type !== 'estimate' && (
                <p className="text-green-700 font-semibold flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />{job.price_snapshot}
                  {effectivePay ? <span className="text-gray-500 font-normal text-xs">· crew pay ${effectivePay}</span> : ''}
                </p>
              )}
              {job.crew_notes_snapshot && <p className="text-orange-600 text-xs italic">{job.crew_notes_snapshot}</p>}
            </div>
            <div className="px-5 pb-4 space-y-2">
              <button onClick={() => onOpen(job)} className="btn-primary w-full justify-center">{job.service_type === 'estimate' ? 'Open visit' : 'Open Job Detail'}</button>
              <div className="flex gap-2">
                <button onClick={() => { onDuplicate(job); onClose(); }}
                  className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
                  <Copy className="h-3.5 w-3.5" /> Duplicate
                </button>
                <button onClick={() => { if (job) onDeleteRequest(job); }}
                  className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg px-3 py-2 hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── To-do sheet ──────────────────────────────────────────────────────────────
// An errand on a day: title, when, a note. Lands on the calendar as a grey
// card; nobody is billed or paid, no route is involved.
/**
 * A quote visit, straight from the calendar (owner, Sep 16 2026: "on calendar
 * allow me to add a quote visit as well"). Same endpoint the bidder's own page
 * uses, so the visit shows up in his list and on the phone feed too.
 */
function QuoteVisitSheet({ date, open, onClose, onAdded, presetCustomerId }: { date: string; open: boolean; onClose: () => void; onAdded: () => void; presetCustomerId?: number }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const [day, setDay] = useState(date);
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [fresh, setFresh] = useState({ name: '', phone: '', address: '', city: '' });
  const [mode, setMode] = useState<'existing' | 'new'>('existing');

  useEffect(() => {
    if (open) { setDay(date); setTime(''); setNotes(''); setSearch(''); setCustomerId(presetCustomerId || null); setMode('existing'); setFresh({ name: '', phone: '', address: '', city: '' }); }
  }, [open, date, presetCustomerId]);
  // Arriving straight from "customer saved → book a quote visit": show who it is for.
  useEffect(() => {
    if (open && presetCustomerId && clientsRef.current) {
      const c = clientsRef.current.find((x: any) => x.id === presetCustomerId);
      if (c) setSearch(c.name);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetCustomerId]);
  const clientsRef = useRef<any[] | null>(null);

  // The picker hands back every client (names only); the typing filters here.
  const { data: clients } = useQuery('quote-visit-clients', () =>
    api.get('/quotes/pickers/customers').then(r => (Array.isArray(r.data) ? r.data : r.data?.customers || [])),
    { enabled: open && mode === 'existing', staleTime: 300000, onSuccess: (rows: any[]) => {
      clientsRef.current = rows;
      if (presetCustomerId) { const c = rows.find((x: any) => x.id === presetCustomerId); if (c) setSearch(c.name); }
    } });
  const needle = search.trim().toLowerCase();
  const found = needle.length > 1
    ? (clients || []).filter((c: any) => String(c.name || '').toLowerCase().includes(needle)).slice(0, 6)
    : [];

  const add = useMutation(
    () => api.post('/quotes/visits', {
      date: day,
      time_start: time || undefined,
      customer_id: mode === 'existing' ? customerId : undefined,
      new_client: mode === 'new' ? { name: fresh.name, phone: fresh.phone, address: fresh.address, city: fresh.city } : undefined,
      notes: notes || undefined,
    }),
    {
      onSuccess: (r: any) => { toast.success(r.data?.message || 'Quote visit booked'); onAdded(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not book that visit'); },
    }
  );

  if (!open) return null;
  const ready = !!day && (mode === 'existing' ? !!customerId : fresh.name.trim().length > 1);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold text-gray-900">Quote visit</h3><button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button></div>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
          <button onClick={() => setMode('existing')} className={`press flex-1 py-2 ${mode === 'existing' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>A client we have</button>
          <button onClick={() => setMode('new')} className={`press flex-1 py-2 border-l border-gray-300 ${mode === 'new' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>Someone new</button>
        </div>
        {mode === 'existing' ? (
          <>
            <input className="input" placeholder="Search a name or address" value={search} onChange={e => { setSearch(e.target.value); setCustomerId(null); }} autoFocus />
            {found.map((c: any) => (
              <button key={c.id} onClick={() => { setCustomerId(c.id); setSearch(c.name); }}
                className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${customerId === c.id ? 'border-brand-500 bg-brand-50' : 'border-gray-200'}`}>
                <span className="font-medium text-gray-900">{c.name}<AccountMark mark={c.account_mark} /></span>
                {c.address && <span className="text-gray-500"> · {c.address}</span>}
              </button>
            ))}
          </>
        ) : (
          // The same questions as everywhere else; once saved they are "a client we have".
          <CustomerIntake compact source="quote_visit" submitLabel="Save, then pick the day"
            onSaved={out => { setCustomerId(out.customer.id); setSearch(out.customer.name); setMode('existing'); toast.success(`${out.customer.name} added`); }} />
        )}
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-gray-600">Day<input type="date" className="input mt-1" value={day} onChange={e => setDay(e.target.value)} /></label>
          <label className="text-xs text-gray-600">Time (optional)<input type="time" className="input mt-1" value={time} onChange={e => setTime(e.target.value)} /></label>
        </div>
        <textarea className="input" rows={2} placeholder="What do they want looking at? (optional)" value={notes} onChange={e => setNotes(e.target.value)} />
        <button onClick={() => add.mutate()} disabled={readOnly || (!ready || add.isLoading)} className="press w-full py-3 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
          {add.isLoading ? 'Booking…' : 'Put it on the calendar'}
        </button>
        <p className="text-[11px] text-gray-500 text-center">Blue on the calendar. It shows in Quotes and on the phone feed, with a 30-minute alarm.</p>
      </div>
    </div>
  );
}

/**
 * The phone calendar feed. Subscribing once puts every quote visit, project,
 * errand and route block on the phone's own calendar — and each one brings a
 * 30-minute alarm with it, which is what the owner actually asked for.
 */
function PhoneFeedSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const [copied, setCopied] = useState(false);
  const { data, refetch, isLoading } = useQuery('calendar-feed-link', () => api.get('/calendar/link').then(r => r.data), { enabled: open });
  const make = useMutation(() => api.post('/calendar/link').then(r => r.data), {
    onSuccess: () => { refetch(); toast.success('New link made'); },
    onError: () => { toast.error('Could not make a link'); },
  });
  if (!open) return null;
  const url: string | null = data?.url || null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold text-gray-900">On my phone</h3><button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button></div>
        <p className="text-sm text-gray-600">
          Subscribe your phone to this and today's quote visits, projects, errands and route blocks appear in your own
          calendar — each with an alarm 30 minutes before.
        </p>
        {isLoading ? (
          <p className="text-sm text-gray-500">Checking…</p>
        ) : url ? (
          <>
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-[11px] font-mono break-all text-gray-700">{url}</div>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard?.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); }}
                className="press flex-1 py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold">{copied ? 'Copied' : 'Copy the link'}</button>
              <a href={data?.webcal || url} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold text-center">Add it now</a>
            </div>
            <p className="text-[11px] text-gray-500">
              iPhone: Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar, then paste it.
              Android: calendar.google.com → Other calendars → From URL.
            </p>
            <button onClick={() => make.mutate()} disabled={readOnly || (make.isLoading)} className="text-xs text-gray-500 underline">
              Make a new link (the old one stops working)
            </button>
          </>
        ) : (
          <button onClick={() => make.mutate()} disabled={readOnly || (make.isLoading)} className="press w-full py-3 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
            {make.isLoading ? 'Making…' : 'Make my calendar link'}
          </button>
        )}
        <p className="text-[11px] text-gray-500">Anyone with the link can read your schedule — keep it to yourself.</p>
      </div>
    </div>
  );
}

/**
 * Route the loose jobs on a day.
 *
 * The owner, Sep 17 2026: "I have five lawns today, all separate jobs, not on a
 * route — I want to be able to route them." Pick the jobs and who is doing
 * them, see the order and the clock, and build it: a one-day route the crew
 * app, the route runner and drive pay all treat like any other.
 *
 * A time a customer was promised is planned around — and the preview says, per
 * stop, whether it holds. The list of jobs used to print "Promised 10:00 AM —
 * kept" under each one before any order had been worked out, and went on
 * saying it while the preview below showed that stop an hour late (owner, Oct
 * 5 2026: "I want the optimize and re route button to actually work"). The
 * list now only says what was promised; "Show the order" says what happens to
 * it, from the same planner the Field page uses (POST /routes/build-day).
 *
 * Or nobody is picked and the day goes out as ONE open shift (owner, Oct 8
 * 2026: "can you change the open shift thing somehow implement ith multiple
 * dif oibs in one day to get the guys to move to the next job without having
 * to request another open shift thing myabe a group or route or something").
 * The second choice under "Who's doing it" swaps the name chips for what he
 * already fills in on a job's Open shift panel — People needed, who can claim
 * it, Text them now — and sends them as `offer` with the same call. Same
 * order, same clock, but nobody is on the route until someone claims it; one
 * claim takes every job on it. In that mode the list is the jobs that can go
 * in an open shift (GET /routes/loose-jobs?offer=1): projects are ticked — a
 * project is what an open shift is for — jobs already offered one at a time
 * are listed and ticked (he is swapping three offers for one), and what the
 * server would refuse (a mow, a repeating visit, a quote visit, a job with
 * people already ticked on it…) sits underneath, greyed, with its reason.
 */
function RouteDaySheet({ date, startOffer = false, onClose, onBuilt }: { date: string; startOffer?: boolean; onClose: () => void; onBuilt: (stay?: boolean) => void }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(['loose-jobs', date], () => api.get('/routes/loose-jobs', { params: { date } }).then(r => r.data.jobs as any[]));
  const { data: offerData, isLoading: offerLoading } = useQuery(offerJobsKey(date), () => fetchOfferJobs(date));
  const { data: members } = useQuery('crew-members-all', () => api.get('/crews/members/all').then(r => r.data.members as any[]));
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [crew, setCrew] = useState<Set<number>>(new Set());
  const [start, setStart] = useState('');
  const [preview, setPreview] = useState<any>(null);
  // Offering it as one open shift: its own ticks, and the fields of the job page's Open shift panel.
  const [modePick, setModePick] = useState<'crew' | 'offer' | null>(startOffer ? 'offer' : null);
  const [offerPicked, setOfferPicked] = useState<Set<number>>(new Set());
  const [needed, setNeeded] = useState(1);
  const [to, setTo] = useState<'everyone' | 'picked'>('everyone');
  const [people, setPeople] = useState<number[]>([]);
  const [textNow, setTextNow] = useState(true);
  // What the server said once the day was offered — the sheet stays up to show it.
  const [offered, setOffered] = useState<any>(null);
  // …and, if he gave the day to someone from here, what the server said to that.
  const [given, setGiven] = useState<any>(null);

  useEffect(() => {
    if (data) {
      // Projects are their own trips: listed, but not ticked (owner, Sep 17 2026).
      setPicked(new Set(data.filter((j: any) => !j.project).map((j: any) => j.job_id)));
      const owners = Array.from(new Set(data.map((j: any) => j.assigned_crew_member_id).filter(Boolean)));
      if (owners.length === 1) setCrew(new Set(owners as number[]));
    }
  }, [data]);
  useEffect(() => {
    // As an open shift it is the other way round: the projects are ticked, and so is anything already
    // offered on its own. A treatment that could ride along is listed, for him to tick.
    if (offerData) { setOfferPicked(new Set(offerData.filter(j => !j.cant_offer && (j.project || j.open_shift)).map(j => j.job_id))); setPreview(null); }
  }, [offerData]);

  const jobs: any[] = data || [];
  const canGo = (offerData || []).filter(j => !j.cant_offer);
  const cantGo = (offerData || []).filter(j => !!j.cant_offer);
  // The server refuses to offer a day that has gone by, so the choice is not there to pick.
  const past = date < businessToday();
  const offerable = !past && canGo.length > 0;
  // Nothing loose to route by hand, but jobs already offered one at a time: he is here to put those together.
  const mode: 'crew' | 'offer' = !offerable ? 'crew' : modePick ?? (jobs.length === 0 ? 'offer' : 'crew');
  const offering = mode === 'offer';
  const sel = offering ? offerPicked : picked;
  const needsPeople = offering && to === 'picked' && people.length === 0;
  // An order worked out one way is never built the other way: the button under it says what it will do.
  useEffect(() => { setPreview(null); }, [offering]);

  const body = (dry: boolean) => offering
    // No employee_ids with an offer: nobody is on it until someone claims it.
    ? { date, job_ids: Array.from(offerPicked), start: start || undefined, dry_run: dry,
        offer: { to, required_count: needed, ...(to === 'picked' ? { crew_member_ids: people } : {}), notify: textNow } }
    : { date, job_ids: Array.from(picked), employee_ids: Array.from(crew), start: start || undefined, dry_run: dry };
  // A job that went onto a route, was claimed or changed while the sheet was open: read both lists again.
  const stale = (e: any) => {
    if (e?.response?.status === 409 || e?.response?.data?.code === 'CANT_OFFER') { setPreview(null); qc.invalidateQueries(['loose-jobs', date]); }
  };
  const look = useMutation(() => api.post('/routes/build-day', body(true)).then(r => r.data), {
    onSuccess: (d: any) => setPreview(d),
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not work out the order'); stale(e); },
  });
  const build = useMutation(() => api.post('/routes/build-day', body(false)).then(r => r.data), {
    onSuccess: (d: any) => {
      if (!d?.open_shift) { toast.success(d.message || 'Route built'); onBuilt(); return; }
      // Offered. Texting not set up: the messages to send from his phone are shown here, and left for the
      // Open shift panel of each of its jobs — where Create Job's "Text them now" leaves them too.
      const drafts = d?.notify?.drafts || [];
      if (drafts.length) for (const st of d.stops || []) qc.setQueryData(['open-shift-drafts', String(st.job_id)], drafts);
      qc.invalidateQueries(['loose-jobs', date]);
      qc.invalidateQueries('jobs-list');
      setOffered(d);
      onBuilt(true);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || (offering ? 'Could not offer that open shift' : 'Could not build the route')); stale(e); },
  });

  const dayLabel = new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const toggle = (set: Set<number>, id: number) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); return n; };
  const chooseMode = (m: 'crew' | 'offer') => { setModePick(m); setPreview(null); };
  // Who the preview says it goes to, in the server's reading of the list (someone no longer active is left off).
  const offeredTo = (o: any) => {
    const names: string[] = (o?.people || []).map((p: any) => p.name);
    return o?.to !== 'picked' ? 'anyone on the crew app' : names.length > 3 ? `${names.length} people you picked` : namesWord(names) || 'the people you picked';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Route the loose jobs — {dayLabel}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        {offered ? (
          <>
            {/* Offered: what the server did, in its words, the stops in order, and any texts left to send. */}
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-2">
              <p className="text-sm font-semibold text-orange-900">Offered as one open shift</p>
              <p className="text-sm text-gray-800">{offered.message}</p>
              <ol className="text-sm text-gray-700 space-y-0.5">
                {(offered.stops || []).map((st: any, i: number) => (
                  <li key={st.job_id}>
                    {i + 1}. {clock12(st.arrive)} — <Link to={`/jobs/${st.job_id}`} className="font-medium text-brand-700 underline">{st.customer_name || st.title || `Job ${st.job_id}`}</Link>
                    {st.customer_name && st.title ? <span className="text-gray-500"> · {st.title}</span> : null}
                  </li>
                ))}
              </ol>
              {!offered.notify && <p className="text-xs text-orange-900">Nobody was texted. When you're ready, open any of these jobs and tap "Text the ones not texted yet".</p>}
              {(offered.notify?.skipped || []).length > 0 && (
                <p className="text-xs text-orange-900">Not texted: {offered.notify.skipped.map((k: any) => `${k.name} (${k.why})`).join(', ')}</p>
              )}
              {(offered.notify?.failures || []).length > 0 && (
                <p className="text-xs text-orange-900">Couldn't be reached: {offered.notify.failures.map((k: any) => k.name).join(', ')}</p>
              )}
              <OpenShiftDrafts drafts={offered.notify?.drafts} className="border-t border-orange-200 pt-2" />
              {/* He already knows who's taking it (owner, Oct 22 2026): the day is theirs from here — the same as the
                  job page's Open shift panel. One person; a second spot, or a change of mind, is on the job page. */}
              {(offered.stops || []).length > 0 && (given ? (
                <div className="border-t border-orange-200 pt-2 space-y-2">
                  <p className="text-sm text-gray-800">{given.message}</p>
                  {(given.texts?.skipped || []).length > 0 && (
                    <p className="text-xs text-orange-900">Not texted: {given.texts.skipped.map((k: any) => `${k.name} (${k.why})`).join(', ')}</p>
                  )}
                  <OpenShiftDrafts drafts={given.texts?.drafts} />
                </div>
              ) : (
                <GiveShiftTo
                  className="border-t border-orange-200 pt-2"
                  jobId={offered.stops[0].job_id}
                  claims={[]}
                  required={needed}
                  filled={false}
                  disabled={readOnly}
                  dayWords={`${allJobsWord((offered.stops || []).length)} on ${shortDay(date)}`}
                  onDone={(r) => {
                    if (!r) return;
                    setGiven(r);
                    qc.invalidateQueries('jobs-list');
                    qc.invalidateQueries(['loose-jobs', date]);
                    for (const st of offered.stops || []) {
                      qc.invalidateQueries(['job', String(st.job_id)]);
                      qc.invalidateQueries(['open-shift', String(st.job_id)]);
                    }
                  }}
                />
              ))}
            </div>
            <p className="text-[11px] text-gray-500">To change who it's offered to, take someone off or text again, open any of these jobs and use Open shift — it changes {allJobsWord((offered.stops || []).length)}.</p>
            <button onClick={onClose} className="btn-primary text-sm w-full">Done</button>
          </>
        ) : isLoading || offerLoading ? <p className="text-sm text-gray-500">Loading…</p> : jobs.length === 0 && !offerable ? (
          <p className="text-sm text-gray-500">Every job that day is already on a route.</p>
        ) : (
          <>
            {!offering ? (
            <div className="space-y-1.5">
              {jobs.length === 0 && <p className="text-sm text-gray-500">Nothing else is loose that day — the jobs left are already open shifts.</p>}
              {jobs.map((j: any) => (
                <label key={j.job_id} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${picked.has(j.job_id) ? 'border-brand-400 bg-brand-50' : 'border-gray-200'}`}>
                  <input type="checkbox" className="mt-0.5" checked={picked.has(j.job_id)} onChange={() => { setPicked(p => toggle(p, j.job_id)); setPreview(null); }} />
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-gray-900">{j.customer_name}</span>
                    <span className="text-gray-500"> · {j.title || j.service_type}</span>
                    {j.project && <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-orange-700 bg-orange-50 border border-orange-200 rounded px-1">Project — own trip</span>}
                    <span className="block text-xs text-gray-500 truncate">{[j.address, j.city].filter(Boolean).join(', ')}{j.minutes ? ` · ${j.minutes} min` : ''}</span>
                    {j.firm && <span className="block text-xs font-semibold text-amber-700">⏰ Promised {fmtClock(String(j.time_start).slice(0, 5))}</span>}
                    {!j.pinned && <span className="block text-xs text-amber-700">No map pin yet — the address gets looked up when you build</span>}
                  </span>
                </label>
              ))}
            </div>
            ) : (
            <div className="space-y-1.5">
              {canGo.map(j => (
                <label key={j.job_id} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${offerPicked.has(j.job_id) ? 'border-brand-400 bg-brand-50' : 'border-gray-200'}`}>
                  <input type="checkbox" className="mt-0.5" checked={offerPicked.has(j.job_id)} onChange={() => { setOfferPicked(p => toggle(p, j.job_id)); setPreview(null); }} />
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-gray-900">{j.customer_name}</span>
                    <span className="text-gray-500"> · {j.title || j.service_type}</span>
                    {j.open_shift && <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 whitespace-nowrap">Open shift now</span>}
                    <span className="block text-xs text-gray-500 truncate">{[j.address, j.city].filter(Boolean).join(', ')}{j.minutes ? ` · ${j.minutes} min` : ''}</span>
                    {j.assigned_name && <span className="block text-xs text-amber-700">{j.assigned_name} is on it now and comes off when it's offered</span>}
                    {j.firm && <span className="block text-xs font-semibold text-amber-700">⏰ Promised {fmtClock(String(j.time_start).slice(0, 5))}</span>}
                    {!j.pinned && <span className="block text-xs text-amber-700">No map pin yet — the address gets looked up when you offer it</span>}
                  </span>
                </label>
              ))}
              {cantGo.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-600 pt-1">Can't go in an open shift</p>
                  {cantGo.map(j => (
                    <label key={j.job_id} className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500 cursor-not-allowed">
                      <input type="checkbox" className="mt-0.5" checked={false} disabled readOnly />
                      <span className="flex-1 min-w-0">{whyNotWord(j.cant_offer)}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">Who's doing it</p>
              <div className="space-y-1 mb-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="radio" name="route_day_who" checked={!offering} onChange={() => chooseMode('crew')} className="text-brand-600 focus:ring-brand-500" />
                  People I pick now
                </label>
                <label className={`flex items-center gap-2 text-sm ${offerable ? 'text-gray-700 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}>
                  <input type="radio" name="route_day_who" checked={offering} disabled={!offerable} onChange={() => chooseMode('offer')} className="text-brand-600 focus:ring-brand-500" />
                  Offer it as one open shift
                </label>
                {!offerable && (
                  <p className="text-[11px] text-gray-500 pl-6">
                    {past ? 'That day has already passed — an open shift has to be today or later.' : 'Nothing loose that day can go in an open shift.'}
                  </p>
                )}
              </div>
              {!offering ? (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    {(members || []).filter((m: any) => m.is_active !== 0 && m.role !== 'manager').map((m: any) => (
                      <button key={m.id} type="button" onClick={() => { setCrew(c => toggle(c, m.id)); setPreview(null); }}
                        className={`press px-2.5 py-1 rounded-full border text-xs ${crew.has(m.id) ? 'border-brand-500 bg-brand-600 text-black' : 'border-gray-300 text-gray-700'}`}>{m.name}</button>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">One person is timed slower than two, the same as every route. People can be changed later on the Routes page.</p>
                </>
              ) : (
                // The same three things as a job's Open shift panel, for the whole day.
                <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <label htmlFor="route_day_people_needed" className="text-sm text-gray-600">People needed:</label>
                    <PeopleNeededInput id="route_day_people_needed" value={needed} onChange={n => { setNeeded(n); setPreview(null); }} />
                    <span className="text-[11px] text-gray-500">for the whole day — everyone who claims does every job</span>
                  </div>
                  {/* The day is timed for this many people from the start (the server plans the order below the
                      same way), so the times do not move when someone claims it. */}
                  <p className="text-[11px] text-gray-500">One person is timed slower than two, the same as every route{needed === 1 ? ' — the times below are for one person working alone' : ''}.</p>
                  <div>
                    <p className="label-sm">Who can claim it</p>
                    <div className="space-y-1">
                      {([['everyone', 'Anyone on the crew app'], ['picked', 'Only people I pick']] as const).map(([val, lbl]) => (
                        <label key={val} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input type="radio" name="route_day_offer_to" checked={to === val} onChange={() => { setTo(val); setPreview(null); }}
                            className="text-brand-600 focus:ring-brand-500" />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </div>
                  {to === 'picked' && (
                    <div className="bg-white border border-orange-200 rounded-lg p-2.5">
                      <PeoplePicker value={people} onChange={ids => { setPeople(ids); setPreview(null); }} />
                      {people.length === 0 ? (
                        <p className="text-xs text-amber-700 mt-1.5">Pick at least one person.</p>
                      ) : people.length < needed ? (
                        <p className="text-xs text-amber-700 mt-1.5">{people.length} picked for {needed} needed — it can't fill unless you add more.</p>
                      ) : null}
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={textNow} onChange={e => setTextNow(e.target.checked)}
                      className="rounded border-gray-300 text-brand-600" />
                    Text them now
                  </label>
                  <p className="text-[11px] text-gray-500">One claim takes every job ticked above. Whoever claims it runs the day like a route: Start Route, then the next stop.</p>
                </div>
              )}
            </div>

            <label className="block text-xs text-gray-600">Start (optional — blank is 8:00, or soon if it's today)
              <input type="time" className="input mt-1 w-40" value={start} onChange={e => { setStart(e.target.value); setPreview(null); }} />
            </label>

            <button onClick={() => look.mutate()} disabled={readOnly || (!sel.size || look.isLoading || needsPeople)} className="btn-secondary text-sm w-full">
              {look.isLoading ? 'Working out the order…' : 'Show the order'}
            </button>

            {preview && (
              <div className="rounded-lg border border-gray-200 p-3 space-y-1.5">
                {/* clock12: a plan that runs past midnight reads "12:16 AM tomorrow", never "24:16". */}
                <p className="text-sm text-gray-900">Leave home <b>{clock12(preview.start)}</b>{preview.drive_from_home_minutes ? <> ({preview.drive_from_home_minutes} min to the first yard)</> : null}, about <b>{preview.drive_minutes} min</b> driving in all, done around <b>{clock12(preview.finish)}</b></p>
                {/* The same lines the Field plan shows: a start that has gone by, a start pulled
                    earlier for a promise, and how many promised times this order keeps. */}
                {preview.start_passed && <p className="text-xs font-semibold text-amber-700">{clock12(preview.start_was)} has already gone by — planned from now.</p>}
                {preview.start_early && <p className="text-xs text-gray-600">This starts earlier than usual so a promised time isn't missed.</p>}
                {preview.set_times > 0 && (preview.kept >= preview.set_times
                  ? <p className="text-xs font-semibold text-green-700">{preview.set_times === 1 ? 'The promised time is kept' : `All ${preview.set_times} promised times kept`}</p>
                  : <p className="text-xs font-semibold text-amber-700">{preview.kept} of {preview.set_times} promised time{preview.set_times === 1 ? '' : 's'} kept — the ones that aren't are marked below. Call those customers, or change the start.</p>)}
                <ol className="text-sm text-gray-700 space-y-0.5">
                  {preview.stops.map((st: any, i: number) => (
                    <li key={st.job_id}>
                      {i + 1}. {clock12(st.arrive)} — {st.customer_name}
                      {/* The line already leads with when he gets there, so this only says what was promised and
                          whether it holds: "(promised 10:00 AM)", "(promised 4:00 PM — about 24 min late)", and
                          for a time an hour or more behind "(promised 7:00 AM — can't be kept)" — never a bare
                          "176 min late" (owner, Oct 5 2026). Same rule and colours as the Field plan. */}
                      {st.firm ? <PlanPromised p={{ must_start: st.firm, late_minutes: st.late_minutes, behind_minutes: st.behind_minutes, gone_by: st.gone_by, passed: st.passed, moved: st.moved }} /> : null}
                      {!st.pinned ? <span className="text-amber-700"> (no pin — on the end)</span> : null}
                    </li>
                  ))}
                </ol>
                {preview.pinned_now?.length > 0 && <p className="text-[11px] text-gray-500">Found pins for {preview.pinned_now.join(', ')}.</p>}
                <p className="text-[11px] text-gray-500">Times above are a straight-line estimate; the route page re-times it on real drive times.</p>
                {/* Offered: who it goes to and how many it needs, as the server read them, and who comes off these jobs. */}
                {preview.open_shift && (
                  <p className="text-xs text-orange-900 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1.5">
                    One open shift for {allJobsWord(preview.stops.length)} on {shortDay(date)}: {preview.offer?.required_count || 1} {(preview.offer?.required_count || 1) === 1 ? 'person' : 'people'} needed, offered to {offeredTo(preview.offer)}. Nobody is on it until someone claims it.
                    {preview.taken_off?.length > 0 && ` ${namesWord(preview.taken_off)} ${preview.taken_off.length === 1 ? 'is' : 'are'} on ${preview.stops.length === 1 ? 'it' : 'some of these'} now and ${preview.taken_off.length === 1 ? 'comes' : 'come'} off.`}
                    {textNow ? ' They get one text each for the whole day.' : ' Nobody is texted yet — you can text them later from any of its jobs.'}
                  </p>
                )}
                <button onClick={() => build.mutate()} disabled={readOnly || (build.isLoading)} className="btn-primary text-sm w-full">
                  {preview.open_shift
                    ? (build.isLoading ? 'Offering…' : `Offer ${jobsWord(preview.stops.length)} as one open shift`)
                    : (build.isLoading ? 'Building…' : `Build the route — ${preview.stops.length} stop${preview.stops.length === 1 ? '' : 's'}`)}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

